import { RouteLanding } from "@/components/layout/RouteLanding";
import { VersionWatcher } from "@/components/layout/VersionWatcher";
﻿import { useEffect, useState, lazy, Suspense, Component, type ComponentType, type ReactNode } from "react";
import { trackEvent } from "@/lib/analytics";

// SPA-CHUNK-SPLIT v01 — route-level code splitting.
// Page components below are wrapped in lazyP() so each becomes its own chunk
// instead of riding in the 12MB entry bundle. lazyP also registers the import
// factory so the ?nochunks=1 escape hatch can eager-preload every chunk on
// mount (see NoChunksPreloader). NOTE: a runtime flag cannot literally restore
// a single monolithic bundle (split is decided at build time) — true rollback
// is Vercel "promote previous deployment". ?nochunks=1 only disables the lazy
// WAIT behavior by fetching everything up front.
const __lazyFactories: Array<() => Promise<unknown>> = [];
function lazyP<T extends ComponentType<any>>(factory: () => Promise<{ default: T }>) {
  __lazyFactories.push(factory);
  return lazy(factory);
}
const __nochunks = (() => {
  try { return new URLSearchParams(window.location.search).get("nochunks") === "1"; }
  catch { return false; }
})();

// Branded Suspense fallback — centered MentionFox amber mark, no layout shift.
function RouteFallback() {
  return (
    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div
        aria-label="Loading"
        style={{
          width: 36, height: 36, borderRadius: "50%",
          border: "3px solid rgba(245,158,11,0.25)", borderTopColor: "#f59e0b",
          animation: "mfspin 0.7s linear infinite",
        }}
      />
      <style>{"@keyframes mfspin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}

// STALE-CHUNK AUTO-RECOVERY (2026-06-05). A deploy re-hashes the lazy route
// chunks; an already-open tab's dynamic import then 404s with "Failed to fetch
// dynamically imported module". That is NOT a real runtime error — the fix is to
// reload ONCE and fetch the fresh chunk hashes. So this boundary now splits the
// two cases:
//   • chunk-load failure  → silent reload once (once-guarded via sessionStorage);
//     if it STILL fails after that reload, show a friendly "new version" card with
//     a Refresh button (never loops).
//   • any other runtime error → unchanged FAIL-VISIBLE card with the real error
//     text + manual Reload (the 2026-06-02 behavior — real bugs stay visible).
// The once-guard is cleared a few seconds after a successful render (see
// ChunkReloadFlagCleaner) so a LATER deploy gets its own single silent reload.
const CHUNK_RELOAD_FLAG = "chunk-reload-attempted";
const CHUNK_ERROR_RE = /failed to fetch dynamically imported module|loading chunk [\w/-]* failed|importing a module script failed|error loading dynamically imported module/i;
function isChunkLoadError(err: unknown): boolean {
  const msg = String((err as any)?.message || err || "");
  return CHUNK_ERROR_RE.test(msg);
}
function chunkReloadAlreadyTried(): boolean {
  try { return sessionStorage.getItem(CHUNK_RELOAD_FLAG) === "1"; } catch { return false; }
}

class ChunkErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean; isChunk: boolean; reloading: boolean; errName: string; errMsg: string; errStack: string; compStack: string }> {
  state = { failed: false, isChunk: false, reloading: false, errName: "", errMsg: "", errStack: "", compStack: "" };
  static getDerivedStateFromError(err: unknown) {
    const name = String((err as any)?.name || "Error");
    const msg = String((err as any)?.message || err || "Unknown error");
    const stack = String((err as any)?.stack || "");
    if (isChunkLoadError(err)) {
      // First stale-chunk failure → reloading:true (silent loader until the
      // reload in componentDidCatch fires). Already reloaded once → friendly card.
      return { failed: true, isChunk: true, reloading: !chunkReloadAlreadyTried(), errName: name, errMsg: msg, errStack: stack };
    }
    return { failed: true, isChunk: false, reloading: false, errName: name, errMsg: msg, errStack: stack };
  }
  componentDidCatch(err: unknown, info: { componentStack?: string }) {
    if (isChunkLoadError(err)) {
      if (!chunkReloadAlreadyTried()) {
        // New deploy re-hashed the chunks; set the once-guard and reload to fetch
        // the fresh hashes. Never reloads a second time (guard blocks the loop).
        try { sessionStorage.setItem(CHUNK_RELOAD_FLAG, "1"); } catch { /* ignore */ }
        window.location.reload();
        return;
      }
      console.error("[mf] chunk reload already attempted; showing refresh prompt:", (err as any)?.message);
      return;
    }
    // Non-chunk runtime error — capture the React component stack for devtools.
    this.setState({ compStack: String(info?.componentStack || "") });
    console.error("[mf] render error:", (err as any)?.name, (err as any)?.message, (err as any)?.stack, info?.componentStack);
  }
  render() {
    if (this.state.failed) {
      if (this.state.isChunk) {
        // Brief moment before window.location.reload() takes effect — show the
        // normal route loader, not an error, so the silent reload feels seamless.
        if (this.state.reloading) return <RouteFallback />;
        // Reloaded once and the import STILL fails → friendly prompt, no loop.
        return (
          <div style={{ minHeight: "60vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, padding: 24, fontFamily: "system-ui, sans-serif", textAlign: "center" }}>
            <p style={{ color: "#444", fontWeight: 600, margin: 0 }}>A new version is available.</p>
            <p style={{ color: "#6b7280", fontSize: 13, margin: 0 }}>Please refresh to load the latest version.</p>
            <button onClick={() => { try { sessionStorage.removeItem(CHUNK_RELOAD_FLAG); } catch { /* ignore */ } window.location.reload(); }} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #f59e0b", background: "#f59e0b", color: "#fff", cursor: "pointer" }}>
              Refresh
            </button>
          </div>
        );
      }
      const frames = [this.state.errStack, this.state.compStack].filter(Boolean).join("\n— component stack —\n");
      return (
        <div style={{ minHeight: "60vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, padding: 24, fontFamily: "system-ui, sans-serif", textAlign: "center" }}>
          <p style={{ color: "#444", fontWeight: 600, margin: 0 }}>Something failed to load on this page.</p>
          <pre style={{ color: "#b91c1c", fontSize: 12, maxWidth: 720, whiteSpace: "pre-wrap", wordBreak: "break-word", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 12px", margin: 0 }}>
            {this.state.errName}: {this.state.errMsg}
          </pre>
          {frames && (
            <pre style={{ color: "#6b7280", fontSize: 11, textAlign: "left", maxWidth: 720, maxHeight: 220, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 12px", margin: 0 }}>
              {frames}
            </pre>
          )}
          <button onClick={() => window.location.reload()} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #f59e0b", background: "#f59e0b", color: "#fff", cursor: "pointer" }}>
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Clears the stale-chunk once-guard a few seconds after a successful render, so a
// LATER deploy gets its own single silent reload. Delayed (not on mount) so a
// reload that immediately re-fails still sees the flag set and shows the friendly
// card instead of looping.
// T506: a lazy chunk whose file is gone after a deploy can fail while being PRELOADED
// (route prefetch, an event handler), which never reaches ChunkErrorBoundary. Vite says so
// with a vite:preloadError event; it gets the same single reload, under the same guard.
if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", (ev) => {
    if (chunkReloadAlreadyTried()) return;
    ev.preventDefault();
    try { sessionStorage.setItem(CHUNK_RELOAD_FLAG, "1"); } catch { /* ignore */ }
    window.location.reload();
  });
}

function ChunkReloadFlagCleaner() {
  useEffect(() => {
    const t = setTimeout(() => { try { sessionStorage.removeItem(CHUNK_RELOAD_FLAG); } catch { /* ignore */ } }, 2500);
    return () => clearTimeout(t);
  }, []);
  return null;
}

// ?nochunks=1 escape hatch — eager-preload every lazy chunk on mount so no
// route ever suspends (closest runtime analog to the pre-split monolith).
function NoChunksPreloader() {
  useEffect(() => {
    if (!__nochunks) return;
    __lazyFactories.forEach((f) => { try { f(); } catch { /* ignore */ } });
  }, []);
  return null;
}

// Global error logger — LOG ONLY (2026-06-02). Dynamic-import failures during
// route prefetch / event handlers don't always reach the React boundary; we
// console.error them so the real cause is visible in devtools. It NEVER reloads
// and NEVER touches any flag — fail-visible, never auto-recover.
let __errLoggerInstalled = false;
function installGlobalErrorLogger() {
  if (__errLoggerInstalled || typeof window === "undefined") return;
  __errLoggerInstalled = true;
  window.addEventListener("error", (e: any) => {
    const msg = (e?.error && (e.error.message || e.error)) || e?.message || "";
    if (msg) console.error("[mf] window error:", msg);
  });
  window.addEventListener("unhandledrejection", (e: any) => {
    const r = e?.reason;
    const msg = (r && (r.message || r)) || "";
    if (msg) console.error("[mf] unhandled rejection:", msg);
  });
}
installGlobalErrorLogger();
import { Toaster } from "@/components/ui/toaster";
import { InAppFooter, Footer } from "@/components/Footer";
import { ArsenalModalProvider } from "@/components/ArsenalModal";
const BattlegroundViewer = lazyP(() => import("./pages/BattlegroundViewer"));
const DossierView = lazyP(() => import("./pages/DossierView"));
const DossierPage = lazyP(() => import("./pages/DossierPage"));
const SavedDossiers = lazyP(() => import("./pages/SavedDossiers"));
const ComparisonReportPage = lazyP(() => import("./pages/ComparisonReportPage"));
const CompareHub = lazyP(() => import("./pages/CompareHub"));
const ComparePeoplePage = lazyP(() => import("./pages/ComparePeoplePage"));
const CandidateEvaluatePage = lazyP(() => import("./pages/CandidateEvaluatePage"));
const TalentFinderPage = lazyP(() => import("./pages/TalentFinderPage"));
const StealthApproachPage = lazyP(() => import("./pages/StealthApproachPage"));
const AutomationsPage = lazyP(() => import("./pages/AutomationsPage"));
const BulkCompareCandidatesPage = lazyP(() => import("./pages/BulkCompareCandidatesPage"));
const BulkCompareResultsPage = lazyP(() => import("./pages/BulkCompareResultsPage"));
const TalentCrmPage = lazyP(() => import("./pages/TalentCrmPage"));
// CRM v0 — HubSpot displacement (2026-05-10)
const CrmContacts = lazyP(() => import("./pages/crm/CrmContacts"));
const PipelineCrmPage = lazyP(() => import("./pages/dashboard/CrmPage"));
const CrmHomePage = lazyP(() => import("./pages/dashboard/CrmHomePage"));
const CardHealth = lazyP(() => import("./pages/admin/CardHealth"));
const RawIntake = lazyP(() => import("./pages/admin/RawIntake"));
const ExperienceStore = lazyP(() => import("./pages/admin/ExperienceStore"));
const FounderStack = lazyP(() => import("./pages/dashboard/founder/FounderStack"));
const SenderSettingsPage = lazyP(() => import("./pages/dashboard/pipeline-crm/SenderSettingsPage"));
// NAV-WAVE-1 (2026-05-19): consolidated hub pages for Pipeline / Signals
// / Outreach. Each hub wraps existing page components in a tabs UI so
// every old URL keeps working via redirects below.
const PipelineHubPage = lazyP(() => import("./pages/PipelineHubPage"));
const SignalsHubPage = lazyP(() => import("./pages/SignalsHubPage"));
const OutreachHubPage = lazyP(() => import("./pages/OutreachHubPage"));
// INVESTOR-QUICKFIRE UI (2026-05-19)
const SectorThesesPage = lazyP(() => import("./pages/SectorThesesPage"));
const PortfolioPage = lazyP(() => import("./pages/PortfolioPage"));
const CrmCompanies = lazyP(() => import("./pages/crm/CrmCompanies"));
const CrmDeals = lazyP(() => import("./pages/crm/CrmDeals"));
const CrmPipelines = lazyP(() => import("./pages/crm/CrmPipelines"));
const ApiWebhooksPage = lazyP(() => import("./pages/ApiWebhooksPage"));
const ApisIntegrationsHub = lazyP(() => import("./pages/ApisIntegrationsHub"));
const CommandCenter = lazyP(() => import("./pages/CommandCenter"));
const CrmLists = lazyP(() => import("./pages/crm/CrmLists"));
const CrmWorkflows = lazyP(() => import("./pages/crm/CrmWorkflows"));
const CrmInbox = lazyP(() => import("./pages/crm/CrmInbox"));
const CrmReports = lazyP(() => import("./pages/crm/CrmReports"));
const CrmImportHubSpot = lazyP(() => import("./pages/crm/CrmImportHubSpot"));
const CallCommander = lazyP(() => import("./pages/crm/CallCommander"));
const CallCommanderSettings = lazyP(() => import("./pages/crm/CallCommanderSettings"));
const CallCommanderMobile = lazyP(() => import("./pages/crm/CallCommanderMobile"));
const MarketsLandingPage = lazyP(() => import("./pages/MarketsLandingPage"));
const MarketsCryptoTokenPage = lazyP(() => import("./pages/MarketsCryptoTokenPage"));
const MarketsCompareCryptoPage = lazyP(() => import("./pages/MarketsCompareCryptoPage"));
const EvaluationBundleComparisonPage = lazyP(() => import("./pages/EvaluationBundleComparisonPage"));
const HrToolsPlaceholder = lazyP(() => import("./pages/HrToolsPlaceholder"));
const InfluencerCalculator = lazyP(() => import("./pages/InfluencerCalculator"));
const LandingPages = lazyP(() => import("./pages/LandingPages"));
// NAV Restructure (2026-05-11) — master pages for verb-grouped sidebar.
// VettingSuiteMaster collects all 13 vetters; SetupMaster collects all
// 11 setup surfaces. Existing vetter and setup URLs continue to work.
const VettingSuiteMaster = lazyP(() => import("./pages/VettingSuiteMaster"));
const SetupMaster = lazyP(() => import("./pages/SetupMaster"));
const ContentRepurposing = lazyP(() => import("./pages/ContentRepurposing"));
const InfluencerEvalPage = lazyP(() => import("./pages/InfluencerEvalPage"));
const GettingStarted = lazyP(() => import("./pages/GettingStarted"));
const EvaluationSharePage = lazyP(() => import("./pages/EvaluationSharePage"));
import { TopBar } from "@/components/layout/TopBar";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { UpgradeMomentHost } from "@/components/billing/UpgradeMoment";
const ArsenalViewer = lazyP(() => import("./pages/ArsenalViewer"));
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Index from "./pages/Index";
const OutFoxerReport = lazyP(() => import("./pages/OutFoxerReport"));
// NO /pricing ROUTE, AND THERE never WAS ONE. mentionfox.com/pricing is served
// by public/pricing.html — a static page. src/pages/Pricing.tsx was imported
// here and never rendered, and it was the source of three wrong conclusions
// about what the live page says. Deleted 2026-08-09. Links to /pricing must be
// full page loads (<a href> or window.location), never client-side navigation,
// or the router answers with NotFound instead of the real page.
import Auth from "./pages/Auth";
const ForgotPassword = lazyP(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyP(() => import("./pages/ResetPassword"));
const Dashboard = lazyP(() => import("./pages/Dashboard"));
// UX-1 (2026-05-16): persona-aware /dashboard redirect — founder track
// routes to /dashboard/my-den.
import FounderTrackRedirect from "./components/FounderTrackRedirect";
const CompBuilder = lazyP(() => import("./pages/CompBuilder"));
const ApplicantScreen = lazyP(() => import("./pages/ApplicantScreen"));
const AdminBeMyGuest = lazyP(() => import("./pages/AdminBeMyGuest"));
// Walkthrough-as-Spec v0 (2026-05-10) — record product walkthroughs,
// generate transferable specs (Cursor / Lovable / v0 / Claude.md / JSON)
// + AI-detected issues. Pro+ feature.
const AdminWalkthroughSpecs = lazyP(() => import("./pages/AdminWalkthroughSpecs"));
const Walkthroughs = lazyP(() => import("./pages/Walkthroughs"));
const Onboarding = lazyP(() => import("./pages/Onboarding"));
const TwitterOnboarding = lazyP(() => import("./pages/TwitterOnboarding"));
// Dispatch D (2026-05-23) — investor onboarding wizard at /onboarding/investor.
const InvestorOnboarding = lazyP(() => import("./pages/onboarding/InvestorOnboarding"));
const Mentions = lazyP(() => import("./pages/Mentions"));
const Insights = lazyP(() => import("./pages/Insights"));
const InvestorVetter = lazyP(() => import("./pages/InvestorVetter"));
const CCQueue = lazyP(() => import("./pages/CCQueue"));
const InvestorDossier = lazyP(() => import("./pages/investor-vetter/InvestorDossier"));
const InvestorCompare = lazyP(() => import("./pages/investor-vetter/InvestorCompare"));
const TermSheetAnalyzer = lazyP(() => import("./pages/investor-vetter/TermSheetAnalyzer"));
const MeetingPrep = lazyP(() => import("./pages/investor-vetter/MeetingPrep"));
const PitchDeckReview = lazyP(() => import("./pages/investor-vetter/PitchDeckReview"));
const InvestorBrowse = lazyP(() => import("./pages/investor-vetter/InvestorBrowse"));
// Alumni Spinout Tracker Phase 1 (2026-05-11) — track companies whose
// alumni you want to monitor for spinouts. Lives in Investor Vetter,
// FINANCIAL nav group. Parity surface at /clients/:id/...
const AlumniWatchlists = lazyP(() => import("./pages/investor-vetter/AlumniWatchlists"));
const FounderVetter = lazyP(() => import("./pages/FounderVetter"));
const InfluencerVetter = lazyP(() => import("./pages/InfluencerVetter"));
// YC-1 (2026-05-16): free YC Insider Report — public landing + auth-gated content.
const YcReportLanding = lazyP(() => import("./pages/YcReportLanding"));
const YcReportContent = lazyP(() => import("./pages/YcReportContent"));
// ACC-1 (2026-05-16): accelerator vetter — pick from accelerator_catalog,
// 16-section full report via the generate-vetting Hono bundle.
const AcceleratorVetter = lazyP(() => import("./pages/AcceleratorVetter"));
// YC-SCORER-1 (2026-05-16): 10-dimension YC application scorer + viral share card.
const YcApplicationScorer = lazyP(() => import("./pages/YcApplicationScorer"));
const YcScoreCard = lazyP(() => import("./pages/YcScoreCard"));
// FOUNDER-SELF (2026-05-16): self-vet route reusing the founder vetter
// generator with framing='self_view'.
const FounderSelfVetter = lazyP(() => import("./pages/FounderSelfVetter"));
// INV-MATCH-2 (2026-05-16): Founder Fit matcher (form + result + public share).
const FounderFit = lazyP(() => import("./pages/FounderFit"));
const FounderFitShare = lazyP(() => import("./pages/FounderFitShare"));
// Vetters 0.16 (2026-05-09): Journalist + Source vetters archived (replaced
// by unified JournalismPersonVetter). Donor, Expert Witness, Counterparty,
// Litigation Funder, Insurance Underwriting, Crypto Coin, Penny Stock, NFT
// Collection, SPAC Sponsor, Pre-IPO Secondary, Specific Fund vetter pages
// archived to docs/archived-vetters/2026-05-09/. Imports removed; routes
// below redirect to the dashboard with a graceful fallback.
// SOURCE-CHECK (2026-07-27): one implementation reached from two entry
// points — the Journalism suite nav and the Vetting Suite master card.
const SourceCheck = lazyP(() => import("./pages/SourceCheck"));
const JournalismPersonVetter = lazyP(() => import("./pages/JournalismPersonVetter"));
const PublicationVetter = lazyP(() => import("./pages/PublicationVetter"));
const ExecutiveVetter = lazyP(() => import("./pages/ExecutiveVetter"));
const WealthAdvisorVetter = lazyP(() => import("./pages/WealthAdvisorVetter"));
const PhysicianVetter = lazyP(() => import("./pages/PhysicianVetter"));
const PeFirmVetter = lazyP(() => import("./pages/PeFirmVetter"));
const CompanyVetter = lazyP(() => import("./pages/CompanyVetter"));
const VcFirmVetter = lazyP(() => import("./pages/VcFirmVetter"));
const ClaimsCheck = lazyP(() => import("./pages/ClaimsCheck"));
const ClaimsCheckJob = lazyP(() => import("./pages/ClaimsCheckJob"));
const ManuscriptVerificationView = lazyP(() => import("./pages/ManuscriptVerificationView"));
const CompareCompaniesPage = lazyP(() => import("./pages/CompareCompaniesPage"));
const CompanyComparisonReportPage = lazyP(() => import("./pages/CompanyComparisonReportPage"));
const AppToTargetsDirectory = lazyP(() => import("./pages/AppToTargetsDirectory"));
const AppToTargetDetail = lazyP(() => import("./pages/AppToTargetDetail"));
const AppDraftReview = lazyP(() => import("./pages/AppDraftReview"));
const PaginatedReportTest = lazyP(() => import("./pages/PaginatedReportTest"));
const McpSettings = lazyP(() => import("./pages/McpSettings"));
const PipelineWatch = lazyP(() => import("./pages/PipelineWatch"));
const Foxhole = lazyP(() => import("./pages/Foxhole"));
const FoxholeVoiceSetup = lazyP(() => import("./pages/FoxholeVoiceSetup"));
const DeckQuestionsPage = lazyP(() => import("./pages/DeckQuestionsPage"));
const AntiPortfolioPage = lazyP(() => import("./pages/AntiPortfolioPage"));
const SignalStreamPage = lazyP(() => import("./pages/SignalStreamPage"));
const FoxholePipeline = lazyP(() => import("./pages/FoxholePipeline"));
// Foxhole Investor Dashboard v0.02 wrapper — adds stats / action bar /
// drawers around FoxholePipeline without modifying that page.
const FoxholeDashboardShell = lazyP(() => import("./components/foxhole/FoxholeDashboardShell"));
const PipelineWatchResearch = lazyP(() => import("./pages/PipelineWatchResearch"));
const PipelineWatchCoach = lazyP(() => import("./pages/PipelineWatchCoach"));
const PipelineWatchPortfolio = lazyP(() => import("./pages/PipelineWatchPortfolio"));
const StudioInbox = lazyP(() => import("./pages/StudioInbox"));
const ContentCreatorClipper = lazyP(() => import("./pages/ContentCreatorClipper"));
const BrutalTruthCards = lazyP(() => import("./pages/BrutalTruthCards"));
const ShortsComposer = lazyP(() => import("./pages/ShortsComposer"));
const Settings = lazyP(() => import("./pages/Settings"));
const Sites = lazyP(() => import("./pages/Sites"));
const Brands = lazyP(() => import("./pages/Brands"));
const BrandDetail = lazyP(() => import("./pages/BrandDetail"));
const MyDen = lazyP(() => import("./pages/MyDen"));
const MyDensList = lazyP(() => import("./pages/MyDensList"));
const AddDenPage = lazyP(() => import("./pages/AddDenPage"));
const AgendasPage = lazyP(() => import("./pages/AgendasPage"));
const FindInvestors = lazyP(() => import("./pages/FindInvestors"));
const TweetHunter = lazyP(() => import("./pages/dashboard/TweetHunter"));
const WarmIntros = lazyP(() => import("./pages/WarmIntros"));
const MyThesis = lazyP(() => import("./pages/dashboard/MyThesis"));
const MyCapTable = lazyP(() => import("./pages/dashboard/MyCapTable"));
const AntiInvestors = lazyP(() => import("./pages/dashboard/AntiInvestors"));
// Dispatch D (2026-05-23) — Investor Circuit (co-investors) management page.
const InvestorCircuit = lazyP(() => import("./pages/dashboard/InvestorCircuit"));
const ArtifactsPage = lazyP(() => import("./pages/ArtifactsPage"));
const NeedsReviewPage = lazyP(() => import("./pages/NeedsReviewPage"));
const TermSheetComparator = lazyP(() => import("./pages/TermSheetComparator"));
const InvestorTermSheetAuthor = lazyP(() => import("./pages/invest/InvestorTermSheetAuthor"));
const PortfolioWatch = lazyP(() => import("./pages/invest/PortfolioWatch"));
const PortfolioPulse = lazyP(() => import("./pages/invest/PortfolioPulse"));
const PipelineMatch = lazyP(() => import("./pages/invest/PipelineMatch"));
const PulseWatch = lazyP(() => import("./pages/invest/PulseWatch"));
const DealMemos = lazyP(() => import("./pages/invest/DealMemos"));
const DealMemoDetail = lazyP(() => import("./pages/invest/DealMemoDetail"));
const InvestorUpdates = lazyP(() => import("./pages/invest/InvestorUpdates"));
const DiligenceRooms = lazyP(() => import("./pages/invest/DiligenceRooms"));
const InvestorDecks = lazyP(() => import("./pages/invest/InvestorDecks"));
const DeckShareViewer = lazyP(() => import("./pages/DeckShareViewer"));
const FounderRadar = lazyP(() => import("./pages/invest/FounderRadar"));
const PortfolioTearSheet = lazyP(() => import("./pages/invest/PortfolioTearSheet"));
const InvestorDatarooms = lazyP(() => import("./pages/invest/InvestorDatarooms"));
const DataroomShareViewer = lazyP(() => import("./pages/DataroomShareViewer"));
const InvestorBrief = lazyP(() => import("./pages/invest/InvestorBrief"));
const OutreachTemplates = lazyP(() => import("./pages/invest/OutreachTemplates"));
const InvestLinchpins = lazyP(() => import("./pages/invest/InvestLinchpins"));
import { InvestorOnlyGate } from "./components/invest/InvestorOnlyGate";
// INVESTOR-SUITE-INDEX (2026-05-27) — hub + new sector-breakdown + dealflow-portfolio surfaces.
const InvestHub = lazyP(() => import("./pages/invest/InvestHub"));
const InvestSectionFallback = lazyP(() => import("./pages/invest/InvestSectionFallback"));
const RaisingSim = lazyP(() => import("./pages/invest/RaisingSim"));
const FundSim = lazyP(() => import("./pages/invest/FundSim"));
const InvestSectorTheses = lazyP(() => import("./pages/invest/InvestSectorTheses"));
const InvestPortfolio = lazyP(() => import("./pages/invest/InvestPortfolio"));
const CalendarSettings = lazyP(() => import("./pages/settings/CalendarSettings"));
const HenhousePage = lazyP(() => import("./pages/HenhousePage"));
const FoxPicksHub = lazyP(() => import("./pages/FoxPicksHub"));
import { DenDispatcher } from "./components/dens/DenDispatcher";
// Thirteen surfaces query relations that are absent from the database
// (verified against pg_class). Their routes render an honest page rather
// than the feature's normal empty state, which told users they had not
// used something that had never been able to work. HIDDEN, NOT DELETED —
// the components are untouched; restoring one is deleting a line below.
import SurfaceUnavailable from "./pages/SurfaceUnavailable";
const ClientCompetitors = lazyP(() => import("./pages/ClientCompetitors"));
const NewBrand = lazyP(() => import("./pages/NewBrand"));
const Reports = lazyP(() => import("./pages/Reports"));
const ReportDetail = lazyP(() => import("./pages/ReportDetail"));
const Scans = lazyP(() => import("./pages/Scans"));
const ExecutiveDashboard = lazyP(() => import("./pages/ExecutiveDashboard"));
const Integrations = lazyP(() => import("./pages/Integrations"));
const UpcomingMeetings = lazyP(() => import("./pages/UpcomingMeetings"));
const ScanResults = lazyP(() => import("./pages/ScanResults"));
const NotFound = lazyP(() => import("./pages/NotFound"));
const Embed = lazyP(() => import("./pages/Embed"));
// The MCP connector consent screen. It must live on this origin: the version
// served from the Supabase functions domain could never read the session and
// so could never identify the user.
const ConnectorAuthorize = lazyP(() => import("./pages/ConnectorAuthorize"));
const SavedContent = lazyP(() => import("./pages/SavedContent"));
const Mentioners = lazyP(() => import("./pages/Mentioners"));
const Team = lazyP(() => import("./pages/Team"));
const TeamManagement = lazyP(() => import("./pages/TeamManagement"));
const ClientPortals = lazyP(() => import("./pages/ClientPortals"));
const MonthlyReports = lazyP(() => import("./pages/MonthlyReports"));
const CustomRoles = lazyP(() => import("./pages/CustomRoles"));
const AuditLogs = lazyP(() => import("./pages/AuditLogs"));
const Portal = lazyP(() => import("./pages/Portal"));
const BlogGenerator = lazyP(() => import("./pages/BlogGenerator"));
const FaqGenerator = lazyP(() => import("./pages/FaqGenerator"));
const ContentLibrary = lazyP(() => import("./pages/ContentLibrary"));
const EmbedViewer = lazyP(() => import("./pages/EmbedViewer"));
const Admin = lazyP(() => import("./pages/Admin"));
const AdminUsers = lazyP(() => import("./pages/AdminUsers"));
const AdminActivity = lazyP(() => import("./pages/AdminActivity"));
const AdminFeatureRequests = lazyP(() => import("./pages/AdminFeatureRequests"));
const AdminFoxchatLearning = lazyP(() => import("./pages/AdminFoxchatLearning"));
const FoxChatPreferences = lazyP(() => import("./pages/FoxChatPreferences"));
const SalesArsenal = lazyP(() => import("./pages/SalesArsenal"));
const CaptureForms = lazyP(() => import("./pages/CaptureForms"));
const CaptureForm = lazyP(() => import("./pages/CaptureForm"));
// Videos page removed per Saul 2026-04-22 (nav item never approved).
const VideoView = lazyP(() => import("./pages/VideoView"));
const Proof = lazyP(() => import("./pages/Proof"));
import { installFoxChatGlobals, registerNavigate } from "@/lib/foxchat-dom";
import { usePageViewTracker } from "@/lib/page-view-tracker";
// ResourceCenter import removed 2026-04-30 (sprint 2 P5 — was rendering
// a duplicate floating Foxy alongside HelpChatbot). Component file kept
// at src/components/ResourceCenter.tsx in case the tabs are re-mounted
// elsewhere or merged into HelpChatbot later.
import { HelpChatbot } from "@/components/help/HelpChatbot";
import { InviteClaimer } from "@/lib/inviteAttribution";
import { FoxchatBehaviorHost } from "@/components/FoxchatBehaviorHost";
import { ConnectionAuthDrawer } from "@/components/connections/ConnectionAuthDrawer";
const AdminCostDebug = lazyP(() => import("./pages/AdminCostDebug"));
const AdminSubscriptions = lazyP(() => import("./pages/AdminSubscriptions"));
// AdminImpersonations import removed 2026-05-13 — route unwired but the
// component file stays on disk at src/pages/AdminImpersonations.tsx so
// the feature can be rewired if/when backing tables are created.
const AdminSecurity = lazyP(() => import("./pages/AdminSecurity"));
const AdminAlerts = lazyP(() => import("./pages/AdminAlerts"));
const AdminSettings = lazyP(() => import("./pages/AdminSettings"));
const AdminFounderAlerts = lazyP(() => import("./pages/AdminFounderAlerts"));
const AdminChangelog = lazyP(() => import("./pages/AdminChangelog"));
const Changelog = lazyP(() => import("./pages/Changelog"));
const AdminFoxAgents = lazyP(() => import("./pages/AdminFoxAgents"));
const AdminCronTruth = lazyP(() => import("./pages/AdminCronTruth"));
const AdminMoney = lazyP(() => import("./pages/AdminMoney"));
const AdminMarketplaceOrders = lazyP(() => import("./pages/AdminMarketplaceOrders"));   // T1128
const Owner = lazyP(() => import("./pages/Owner"));
const HealthDashboard = lazyP(() => import("./pages/HealthDashboard"));
const FilteringAnalytics = lazyP(() => import("./pages/FilteringAnalytics"));
const MailboxOAuthCallback = lazyP(() => import("./pages/auth/MailboxOAuthCallback"));
const Competitors = lazyP(() => import("./pages/Competitors"));
const Clips = lazyP(() => import("./pages/Clips"));
const ClipDetail = lazyP(() => import("./pages/ClipDetail"));
const ClipShare = lazyP(() => import("./pages/ClipShare"));
const ClipEmbed = lazyP(() => import("./pages/ClipEmbed"));
const WidgetPreviewPage = lazyP(() => import("./pages/WidgetPreviewPage"));
const BrandKits = lazyP(() => import("./pages/BrandKits"));
import { CookieConsent } from "@/components/CookieConsent";
import { ProtectedAdminRoute } from "@/components/auth/ProtectedAdminRoute";
const Contacts = lazyP(() => import("./pages/Contacts"));
const PeopleFinderPage = lazyP(() => import("./pages/PeopleFinderPage"));
const ProfileSettings = lazyP(() => import("./pages/ProfileSettings"));
const CompanyProfile = lazyP(() => import("./pages/CompanyProfile"));
const SettingsBrands = lazyP(() => import("./pages/SettingsBrands"));
const Dealflow = lazyP(() => import("./pages/Dealflow"));
const FoxDensPage = lazyP(() => import("./pages/FoxDensPage"));
const OutreachReview = lazyP(() => import("./pages/OutreachReview"));
const TwitterOutreachReview = lazyP(() => import("./pages/TwitterOutreachReview"));
const SliderBuilder = lazyP(() => import("./pages/SliderBuilder"));
const SliderPreview = lazyP(() => import("./pages/SliderPreview"));
const ComparisonCardGenerator = lazyP(() => import("./pages/ComparisonCardGenerator"));
const TrendChartGenerator = lazyP(() => import("./pages/TrendChartGenerator"));
const Arsenal = lazyP(() => import("./pages/Arsenal"));
// CompareIndex (old hardcoded /compare list) removed 2026-05-30 — /compare is
// the static portal at public/compare/index.html (COMPARE-PORTAL-REBUILD v0.02).
const ClientReportViewer = lazyP(() => import("@/pages/ClientReportViewer"));
const ReportOrder = lazyP(() => import("@/pages/ReportOrder"));
const Sequences = lazyP(() => import("./pages/Sequences"));
const EngagementHQ = lazyP(() => import("./pages/EngagementHQ"));
const TelegramSetup = lazyP(() => import("./pages/TelegramSetup"));
const RedditSetup = lazyP(() => import("./pages/RedditSetup"));
const GeoFixer = lazyP(() => import("./pages/GeoFixer"));
const GeoSeoHub = lazyP(() => import("./pages/GeoSeoHub"));
const FindHub = lazyP(() => import("./pages/FindHub"));
const CompareHubV2 = lazyP(() => import("./pages/CompareHubV2"));
const GeoFixerCrawlerAnalytics = lazyP(() => import("./pages/GeoFixerCrawlerAnalytics"));
const PublicGeoShare = lazyP(() => import("./pages/PublicGeoShare"));
const ClientCrawlerAnalytics = lazyP(() => import("./pages/ClientCrawlerAnalytics"));
const SeoFixer = lazyP(() => import("./pages/SeoFixer"));
const SeoFixerClient = lazyP(() => import("./pages/SeoFixerClient"));
const WhiteLabelSettings = lazyP(() => import("./pages/WhiteLabelSettings"));
const SettingsConnections = lazyP(() => import("./pages/SettingsConnections"));
const EngagementControls = lazyP(() => import("./pages/EngagementControls"));
const BioKeywords = lazyP(() => import("./pages/BioKeywords"));
const SettingsExtension = lazyP(() => import("./pages/SettingsExtension"));
const EmailSettingsPage = lazyP(() => import("./pages/EmailSettingsPage"));
const SendingIdentityPage = lazyP(() => import("./pages/SendingIdentityPage"));
const ImportInvestorsPage = lazyP(() => import("./pages/ImportInvestorsPage"));
const SettingsApiKeys = lazyP(() => import("./pages/SettingsApiKeys"));
const EmbedEmailComponent = lazyP(() => import("./pages/EmbedEmailComponent"));
const EmbedFoxoutReply = lazyP(() => import("./pages/EmbedFoxoutReply"));
const EmbedProfileSnapshot = lazyP(() => import("./pages/EmbedProfileSnapshot"));
const EmbedComparePicker = lazyP(() => import("./pages/EmbedComparePicker"));
const MultichannelSendPage = lazyP(() => import("./pages/MultichannelSendPage"));
const DataQualityPage = lazyP(() => import("./pages/DataQualityPage"));
const AdminLeadClustersPage = lazyP(() => import("./pages/AdminLeadClustersPage"));
const LiveDemoIndex = lazyP(() => import("./pages/LiveDemoIndex"));
const LiveDemoCanvas = lazyP(() => import("./pages/LiveDemoCanvas"));
const PublicLiveDemoView = lazyP(() => import("./pages/PublicLiveDemoView"));
const SettingsPlatforms = lazyP(() => import("./pages/SettingsPlatforms"));
const SettingsFoxpicks = lazyP(() => import("./pages/SettingsFoxpicks"));
const VoiceSettings = lazyP(() => import("./pages/VoiceSettings"));
const TaglineLab = lazyP(() => import("./pages/TaglineLab"));
const BrandKit = lazyP(() => import("./pages/BrandKit"));
const IndexNow = lazyP(() => import("./pages/IndexNow"));
const OutreachSender = lazyP(() => import("./pages/OutreachSender"));
const LeadPool = lazyP(() => import("./pages/LeadPool"));
const SeoSubmissions = lazyP(() => import("./pages/admin/SeoSubmissions"));
const MediumArticles = lazyP(() => import("./pages/admin/MediumArticles"));
const NextSteps = lazyP(() => import("./pages/admin/NextSteps"));
const Decisions = lazyP(() => import("./pages/admin/Decisions"));
const Hallucinations = lazyP(() => import("./pages/admin/Hallucinations"));
const GeoBriefs = lazyP(() => import("./pages/admin/GeoBriefs"));
const PageProposals = lazyP(() => import("./pages/admin/PageProposals"));
const PageReview = lazyP(() => import("./pages/admin/PageReview"));
const ShowworkBench = lazyP(() => import("./pages/admin/ShowworkBench"));
const FeaturedSubmissions = lazyP(() => import("./pages/admin/FeaturedSubmissions"));
const CompanyClaims = lazyP(() => import("./pages/admin/CompanyClaims"));   // T1160
const McpToken = lazyP(() => import("./pages/McpToken"));
const CitationRoi = lazyP(() => import("./pages/admin/CitationRoi"));
const SeoIndexation = lazyP(() => import("./pages/admin/SeoIndexation"));
const SeoAutopilotHistory = lazyP(() => import("./pages/admin/SeoAutopilotHistory"));
// VET-1 (2026-05-16): demand-signal admin page for Coming-Soon vetter requests.
const AdminVetterRequests = lazyP(() => import("./pages/AdminVetterRequests"));
// SCAN-1 (2026-05-16): re-activation UI for dead lead-scanner queries.
const AdminDeadQueries = lazyP(() => import("./pages/AdminDeadQueries"));
// SCAN-2 (2026-05-16): per-provider hit + cost-saved stats for external API caching.
const AdminApiCacheStats = lazyP(() => import("./pages/AdminApiCacheStats"));
// SCAN-4 (2026-05-16): per-scan-type caps + per-user daily Serper budgets.
const AdminScanBudgets = lazyP(() => import("./pages/AdminScanBudgets"));
// SCAN-5 (2026-05-16): rolling gold-rate quality scoring for lead_scanner_v2.
const AdminScanQuality = lazyP(() => import("./pages/AdminScanQuality"));
// SELF-BRAND-SETUP-WIZARD (2026-05-17) — guided onboarding to mark one
// clients row as the user's self_brand. Gated entry for Citation Leads + GEO.
const SetupMyBusiness = lazyP(() => import("./pages/SetupMyBusiness"));
const ManagerDashboard = lazyP(() => import("./pages/ManagerDashboard"));
const SharePage = lazyP(() => import("./pages/SharePage"));
const FoxtrailResults = lazyP(() => import("./pages/FoxtrailResults"));
const Welcome = lazyP(() => import("./pages/Welcome"));
const AdminSeedWelcomeInbox = lazyP(() => import("./pages/AdminSeedWelcomeInbox"));
const AdminInvestorDedupReview = lazyP(() => import("./pages/AdminInvestorDedupReview"));
const AdminDedupQueueReview = lazyP(() => import("./pages/AdminDedupQueueReview"));
const AdminFoxholeReview = lazyP(() => import("./pages/AdminFoxholeReview"));
const AdminFoxholeRedTeam = lazyP(() => import("./pages/AdminFoxholeRedTeam"));
const AdminFoxholeEfficiency = lazyP(() => import("./pages/AdminFoxholeEfficiency"));
const AdminFoxholeOutbound = lazyP(() => import("./pages/AdminFoxholeOutbound"));
const AdminInvestorDbVelocity = lazyP(() => import("./pages/AdminInvestorDbVelocity"));
const AdminPhotoIntegrity = lazyP(() => import("./pages/AdminPhotoIntegrity"));
const LaunchPack = lazyP(() => import("./pages/LaunchPack"));
const AdminLaunchPackQuality = lazyP(() => import("./pages/AdminLaunchPackQuality"));
// Bundle 0.2 — About + Team page generator (2026-05-10)
const AboutPageGeneratorWizard = lazyP(() => import("./pages/AboutPageGeneratorWizard"));
// GEOFixer Anthropic-models audit (2026-05-10)
const AnthropicGeoAudit = lazyP(() => import("./pages/AnthropicGeoAudit"));
// GEOFixer Earned Media outreach (2026-05-10)
const AdminEarnedMedia = lazyP(() => import("./pages/AdminEarnedMedia"));
const Press = lazyP(() => import("./pages/Press"));
const PressRequests = lazyP(() => import("./pages/PressRequests"));
const Newsjack = lazyP(() => import("./pages/Newsjack"));
// SEO submission helper (2026-05-10) — bulk-submit URLs to GSC
const AdminSeoSubmissionHelper = lazyP(() => import("./pages/AdminSeoSubmissionHelper"));
// Public /search page (Dispatch SH-2, 2026-05-14)
const Search = lazyP(() => import("./pages/Search"));
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { Session } from "@supabase/supabase-js";
import {
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/layout/AppSidebar";
import { ImpersonationBanner, getImpersonationData } from "@/components/admin/ImpersonationBanner";
import { AppLayoutContext } from "@/components/layout/AppLayoutContext";
import { ViewAsProvider } from "@/components/layout/ViewAsContext";
import { AdminViewProvider } from "@/components/layout/AdminViewContext";
import type { Tables } from "@/integrations/supabase/types";

import { profilesRead } from "@/lib/profilesRead";
import { onboardingDeferred } from "@/lib/post-auth-route";
import { OnboardingResumeBanner } from "@/components/onboarding/OnboardingResumeBanner";
import { TrialStatusBanner } from "@/components/billing/TrialStatusBanner";
type AppProfile = Tables<"profiles">;

interface AppLayoutProps {
  children: React.ReactNode;
  requireAuth?: boolean;
}

function AppLayout({ children, requireAuth = true }: AppLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, user, isReady } = useAuth();
  const [profile, setProfile] = useState<AppProfile | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [newMentionsCount, setNewMentionsCount] = useState(0);
  const [actionItemsCount, setActionItemsCount] = useState(0);
  const [showScanModal, setShowScanModal] = useState(false);
  const isImpersonating = !!getImpersonationData();

  // Check if Supabase has a stored token — prevents false redirects during remount
  const hasStoredToken = () => {
    try {
      const keys = Object.keys(window.localStorage);
      return keys.some(k => k.startsWith("sb-") && k.endsWith("-auth-token"));
    } catch { return false; }
  };

  // Clear badge counts when visiting relevant pages
  useEffect(() => {
    if (location.pathname === "/dashboard/mentions") setNewMentionsCount(0);
    if (location.pathname === "/dashboard/insights") setActionItemsCount(0);
  }, [location.pathname]);

  // Install window.foxchat.* once so the chatbot can drive the UI from anywhere.
  useEffect(() => {
    installFoxChatGlobals();
    registerNavigate(navigate);
  }, [navigate]);

  // Log route changes to page_views so FoxChat can personalise based on what
  // pages the user has actually touched.
  usePageViewTracker();

  // Load profile when session is available
  useEffect(() => {
    if (!isReady) return;

    if (!session) {
      // Don't redirect if there's a stored token — session is still restoring
      if (hasStoredToken()) return;
      if (requireAuth) {
        // Capture where they were headed so auth can return them there (reward-first).
        const target = location.pathname + location.search;
        const isAuthPage = location.pathname === "/auth";
        navigate(isAuthPage ? "/auth" : ("/auth?next=" + encodeURIComponent(target)));
      }
      setProfileLoaded(true);
      return;
    }

    // Don't reload profile if we already have one for this user
    if (profile && profile.id === session.user.id) {
      setProfileLoaded(true);
      return;
    }

    let cancelled = false;

    (async () => {
      const { data: profileData } = await profilesRead()
        .select("*")
        .eq("id", session.user.id)
        .single();

      if (cancelled) return;

      setProfile(profileData);

      // PH Bundle 0.1 — first-login redirect to /welcome.
      // 2026-05-16 (DASHBOARD-REDIRECT-FIX): added power-user gate.
      // Active power users (Saul has 47 vetting_reports / 100s of leads)
      // were getting yanked to /welcome on every dashboard visit because
      // the original logic only checked unviewed-inbox-count. Now we also
      // count their real-data footprint — if it's >5 across reports/leads
      // /dealflow, they've already onboarded and the welcome cards don't
      // belong in their main flow. They can still reach /welcome via
      // direct URL or the admin seed page.
      try {
        if (location.pathname === "/dashboard" || location.pathname === "/") {
          const md = (session.user.user_metadata || {}) as Record<string, unknown>;
          const isPh = md["signup_source"] === "ph_deal";
          const { data: phRow } = await supabase
            .from("ph_deal_signups" as any)
            .select("user_id")
            .eq("user_id", session.user.id)
            .maybeSingle();
          if (isPh || phRow) {
            const { count: unviewed } = await supabase
              .from("welcome_inbox_items" as any)
              .select("id", { count: "exact", head: true })
              .eq("user_id", session.user.id)
              .is("viewed_at", null);
            if ((unviewed || 0) > 0) {
              // Power-user gate: skip the welcome redirect if the user
              // already has real working data. Threshold of 5 across
              // these three tables is a conservative floor — anyone past
              // it is unambiguously not a first-time visitor.
              const [{ count: reports }, { count: leads }, { count: deals }] = await Promise.all([
                supabase.from("vetting_reports" as any).select("id", { count: "exact", head: true }).eq("user_id", session.user.id),
                supabase.from("citation_leads" as any).select("id", { count: "exact", head: true }).eq("user_id", session.user.id),
                supabase.from("dealflow" as any).select("id", { count: "exact", head: true }).eq("user_id", session.user.id),
              ]);
              const footprint = (reports || 0) + (leads || 0) + (deals || 0);
              if (footprint <= 5) {
                navigate("/welcome", { replace: true });
              }
            }
          }
        }
      } catch (e) {
        // Non-fatal — never block the dashboard render on the redirect probe.
        console.warn("[welcome-inbox-redirect]", e);
      }

      // Onboarding gate — single canonical path is the /onboarding route.
      // LOOP-FIX (2026-06-14): this redirect fires ONLY when ALL THREE hold:
      //   (1) the profile is FULLY LOADED  — `profileData` is a truthy row, not
      //       null/loading. An unfetched/`undefined` onboarding flag is NOT
      //       treated as false, so nothing redirects during the loading window.
      //   (2) the user is genuinely INCOMPLETE — BOTH completion columns are
      //       NOT true. Onboarding.tsx's off-ramp leaves on EITHER column being
      //       true, so we must mirror that exact source of truth here; checking
      //       only `onboarding_complete === false` would ping-pong a user whose
      //       legacy `onboarding_completed` is true back and forth.
      //   (3) we are NOT already on (or inside) /onboarding (pathname guard).
      // Note: /onboarding itself renders OUTSIDE <AppLayout>, so this guard does
      // not even mount there — but the pathname guard is kept as belt-and-braces.
      const obComplete =
        (profileData as any)?.onboarding_complete === true ||
        (profileData as any)?.onboarding_completed === true;
      // T344 (2026-09-30): (4) the sign-in router did NOT just send this person to the page
      //   they asked for (src/lib/post-auth-route.ts onboardingDeferred, this tab session).
      //   A destination comes first; setup is then offered by OnboardingResumeBanner below.
      if (
        profileData &&
        !obComplete &&
        !location.pathname.startsWith("/onboarding") &&
        !onboardingDeferred()
      ) {
        console.log('[APP] redirecting to /onboarding — onboarding not complete');
        navigate("/onboarding", { replace: true });
        return;
      }

      // Load badge counts
      if (profileData) {
        const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
        const [{ count: mentionsCount }, { count: actionsCount }] = await Promise.all([
          supabase
            .from("mentions")
            .select("*", { count: "exact", head: true })
            .eq("user_id", profileData.id)
            .eq("archived", false)
            .gte("created_at", oneDayAgo),
          supabase
            .from("mentions")
            .select("*", { count: "exact", head: true })
            .eq("user_id", profileData.id)
            .eq("archived", false)
            .eq("sentiment", "positive")
            .gt("view_count", 1000),
        ]);
        if (!cancelled) {
          setNewMentionsCount(mentionsCount || 0);
          setActionItemsCount(actionsCount || 0);
        }
      }

      if (!cancelled) setProfileLoaded(true);
    })();

    return () => { cancelled = true; };
  }, [isReady, session?.user?.id, requireAuth, navigate]);

  // Handle sign-out navigation — with delay to avoid race conditions
  useEffect(() => {
    if (isReady && !session && requireAuth && profileLoaded) {
      // Double-check: if token exists in localStorage, session is still restoring — don't redirect
      if (hasStoredToken()) return;
      // Small delay to let session restore complete on remount
      const t = setTimeout(() => {
        if (!session && !hasStoredToken()) navigate("/auth");
      }, 300);
      return () => clearTimeout(t);
    }
  }, [isReady, session, requireAuth, navigate, profileLoaded]);

  // Realtime badge subscription
  useEffect(() => {
    if (!profile) return;
    const channel = supabase
      .channel("sidebar-mentions")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "mentions",
          filter: `user_id=eq.${profile.id}`,
        },
        () => {
          if (window.location.pathname !== "/dashboard/mentions") setNewMentionsCount((p) => p + 1);
          if (window.location.pathname !== "/dashboard/insights") setActionItemsCount((p) => p + 1);
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile]);

  if (!isReady || !profileLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground text-sm font-mono-data">Loading...</p>
        </div>
      </div>
    );
  }

  if (requireAuth && !session && !hasStoredToken()) return null;


  return (
    <AppLayoutContext.Provider value={true}>
      <ViewAsProvider>
      <ArsenalModalProvider>
      <SidebarProvider defaultOpen={true}>
        <ImpersonationBanner />
        <div className={`min-h-screen flex w-full bg-background ${isImpersonating ? "pt-10" : ""}`} style={{ overflow: "hidden" }}>
          <AppSidebar profile={profile} newMentionsCount={newMentionsCount} actionItemsCount={actionItemsCount} />
          <div className="flex-1 flex flex-col min-w-0" style={{ overflow: "hidden" }}>
            <TopBar profile={profile} />
            {/* T950: the trial line (running / ended + Keep Pro) on every signed-in page. It lived only in
                DashboardLayout's own shell, which renders bare children inside AppLayout, so /dashboard and
                the other AppLayout routes never showed it. */}
            {requireAuth && <div className="px-4 pt-2 empty:hidden"><TrialStatusBanner /></div>}
            {/* THE dashboard scroll container (not the window). id + tabindex let lib/landOn.ts scroll
                it and RouteLanding focus inside it. DashboardLayout carries the same id for its own shell. */}
            <main id="app-main" tabIndex={-1} className="flex-1 overflow-y-auto">
              {/* T344: onboarding offered, not forced, after the sign-in router sent someone to their destination. */}
              {requireAuth && profile && (profile as any).onboarding_complete !== true && (profile as any).onboarding_completed !== true && (
                <OnboardingResumeBanner />
              )}
              {children}
            </main>
            {requireAuth ? <InAppFooter /> : <Footer />}
          </div>
        </div>
        {/* Global help surface — floating ? button + on-demand FoxChat window,
            mounted once so every dashboard page inherits them. */}
        {/* 2026-04-30 ALPS sprint 2 P5 — ResourceCenter (blue Foxy) was
            duplicating the orange HelpChatbot at viewport bottom-right.
            Mount removed; FAQs / Walkthroughs / What's New / Request
            content stays accessible via the /help/* routes (HelpIndex
            etc., which all mount HelpChatbot). Re-mount or merge tabs
            into HelpChatbot in a follow-up if floating discoverability
            of those tabs matters. */}
        {/* v1.4.1-hotfix BUG 7 — global HelpChatbot floating circle removed.
            It rendered cyan-blue bottom-right at z-50 and overlapped
            OnboardingChecklist (orange-accent panel at bottom-right z-40),
            producing the "blue badge over orange Foxy" double-bubble Saul
            reported three times. /help/* routes still embed HelpChatbot
            directly when needed; for in-context help, FoxchatBehaviorHost
            still surfaces contextual amber bubbles when triggers fire. */}
        {/* FOUNDER-DEN-CLEANUP (2026-05-21): the floating chat bubble and the
            Record-walkthrough pill are hidden on the Founder Den
            (/dashboard/my-den) so that page stays focused. They stay mounted
            on every other route. */}
        {location.pathname !== "/dashboard/my-den" && <FoxchatBehaviorHost />}
        <ConnectionAuthDrawer />
        {/* The floating MF-site walkthrough recorder button was removed
            (2026-06-10) — recording is done via the browser extension, and the
            button overlapped the Foxy chat bubble bottom-right. */}
      </SidebarProvider>
      </ArsenalModalProvider>
      </ViewAsProvider>
    </AppLayoutContext.Provider>
  );
}

// ProtectedAdminRoute lives in src/components/auth/ProtectedAdminRoute.tsx (2026-09-15).

// DEN-POLISHING v0.03 PART 7 (2026-05-30) — auth gate that sends logged-out
// visitors to the login page (with a return path) instead of a bare 404.
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, isReady } = useAuth();
  const location = useLocation();
  if (!isReady) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to={`/auth?return_to=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return <>{children}</>;
}

const ChangelogJsonRedirect = () => {
  useEffect(() => {
    window.location.replace("https://wnzxffwiiubqvcvnnjbz.supabase.co/functions/v1/changelog-json");
  }, []);
  return null;
};

// T789: /verification-vetter/job/:id -> /claims-check/job/:id (in-app; vercel.json 301s the cold load).
function LegacyClaimsJobRedirect() {
  const location = useLocation();
  const id = location.pathname.split("/").filter(Boolean).pop() || "";
  return <Navigate to={`/claims-check/job/${id}`} replace />;
}

const queryClient = new QueryClient();

// Redirect /#features hash to /pricing
function HashRedirects() {
  useEffect(() => {
    if (window.location.hash === '#features') {
      window.location.replace('/pricing');
    }
  }, []);
  return null;
}

function ReferralTracker() {
  useEffect(() => {
    const ref = document.referrer;
    if (ref) {
      const llmDomains = ['chatgpt.com', 'chat.openai.com', 'claude.ai', 'gemini.google.com', 'perplexity.ai', 'you.com', 'poe.com'];
      if (llmDomains.some(d => ref.includes(d))) {
        try { trackEvent('llm_referral', { source: new URL(ref).hostname }); } catch {}
      }
    }
    const params = new URLSearchParams(window.location.search);
    const utm_source = params.get('utm_source');
    const ref_param = params.get('ref');
    if (utm_source) trackEvent('utm_visit', { source: utm_source, medium: params.get('utm_medium') || '', campaign: params.get('utm_campaign') || '' });
    if (ref_param) trackEvent('ref_visit', { ref: ref_param });
  }, []);
  return null;
}

const App = () => (
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider delayDuration={500}>
      <Toaster />
      <Sonner />
      {/* Every blocked action opens this one upgrade dialog (cc_tasks row 82). */}
      <UpgradeMomentHost />
      <ReferralTracker />
      {/* Invite links: turns a followed ?ref=<invite token> into one attribution
          once the visitor has an account, whatever route they signed in by. */}
      <InviteClaimer />
      <NoChunksPreloader />
      <ChunkReloadFlagCleaner />
      <BrowserRouter>
        {/* T779: one saved "Admin view" switch shared by the app shell and the admin shell. */}
        <AdminViewProvider>
        {/* Land the user on each route they navigate to, in view and focused. */}
        <RouteLanding />
        {/* T506: a tab opened before a deploy reloads itself (idle) or says a newer version is ready. */}
        <VersionWatcher />
        <HashRedirects />
        <ChunkErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Homepage — serves static HTML from /home via full-page render */}
          <Route path="/" element={<Index />} />
          {/* Public pages served as static HTML by Vercel — no React routes needed */}
          <Route path="/auth" element={<Auth />} />
          {/* OAuth consent for the MCP connector. Deliberately outside AppLayout —
              it is a decision screen, not a dashboard page, and it handles its own
              signed-out case by returning here after login with every OAuth
              parameter intact. */}
          <Route path="/connector/oauth/authorize" element={<ConnectorAuthorize />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/arsenal/:id" element={<ArsenalViewer />} />
          <Route path="/battleground/:id" element={<BattlegroundViewer />} />
          <Route path="/report/:id" element={<ClientReportViewer />} />
          <Route path="/report-order/:id" element={<ReportOrder />} />
          <Route path="/comparison/:shareToken" element={<ComparisonReportPage />} />
          <Route path="/evaluation/:shareToken" element={<EvaluationSharePage />} />
          <Route path="/outfoxer/:id" element={<OutFoxerReport />} />
          <Route path="/dossier/:personId" element={<DossierView />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/onboarding-twitter" element={<TwitterOnboarding />} />
          {/* Dispatch D — investor onboarding wizard (auth-gated, dashboard chrome). */}
          <Route path="/onboarding/investor" element={<AppLayout><InvestorOnboarding /></AppLayout>} />
          <Route path="/getting-started/:persona" element={<GettingStarted />} />

          {/* Dashboard routes — wrapped in AppLayout (requireAuth=true) */}
          {/* BUG L (DEN-AND-LEADS-TRUTH 2026-05-18): Dashboard and My Den are
              different surfaces. Dashboard = "what's happening with what I
              already did" (Inbox, campaign health, sends/replies). My Den =
              "what should I do right now" (engage cards, citation leads).
              FounderTrackRedirect wrapper removed from /dashboard so the
              real Dashboard page renders. /welcome keeps the redirect
              because first-load onboarding still benefits from landing on
              the persona Den. */}
          <Route path="/dashboard" element={<AppLayout><Dashboard /></AppLayout>} />
          <Route path="/dashboard/manager" element={<AppLayout><ManagerDashboard /></AppLayout>} />
          {/* PH Bundle 0.1 — Welcome Inbox + admin seed trigger. */}
          <Route path="/welcome" element={<AppLayout><FounderTrackRedirect><Welcome /></FounderTrackRedirect></AppLayout>} />
          <Route path="/dashboard/welcome" element={<Navigate to="/welcome" replace />} />
          {/* NAV v2 (2026-05-11) — /dashboard/inbox redirects into the
              Dashboard sub-tab so the route stays bookmark-able while
              the actual surface lives on /dashboard?tab=inbox. */}
          <Route path="/dashboard/inbox" element={<Navigate to="/dashboard?tab=inbox" replace />} />
          <Route path="/dashboard/admin/seed-welcome-inbox" element={<ProtectedAdminRoute><AppLayout><AdminSeedWelcomeInbox /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/admin/investor-dedup-review" element={<ProtectedAdminRoute><AppLayout><AdminInvestorDedupReview /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/admin/dedup-queue" element={<ProtectedAdminRoute><AppLayout><AdminDedupQueueReview /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/admin/foxhole-review" element={<ProtectedAdminRoute><AppLayout><AdminFoxholeReview /></AppLayout></ProtectedAdminRoute>} />
          {/* /dashboard/admin/foxy-signals removed 2026-06-14 — unanswered-Foxy
              conversation handoff belongs in the getfoxchat operator view, not MF.
              Path now redirects to /dashboard (no longer routes to a page). */}
          <Route path="/dashboard/admin/foxy-signals" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard/admin/foxhole-red-team" element={<ProtectedAdminRoute><AppLayout><AdminFoxholeRedTeam /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/admin/foxhole-efficiency" element={<ProtectedAdminRoute><AppLayout><AdminFoxholeEfficiency /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/admin/foxhole-outbound" element={<AppLayout><AdminFoxholeOutbound /></AppLayout>} />
          <Route path="/dashboard/admin/investor-db-velocity" element={<ProtectedAdminRoute><AppLayout><AdminInvestorDbVelocity /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/admin/photo-integrity" element={<ProtectedAdminRoute><AppLayout><AdminPhotoIntegrity /></AppLayout></ProtectedAdminRoute>} />
          {/* VET-1: Coming-Soon vetter priority-request aggregator. */}
          <Route path="/admin/vetter-requests" element={<ProtectedAdminRoute><AppLayout><AdminVetterRequests /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/admin/vetter-requests" element={<ProtectedAdminRoute><AppLayout><AdminVetterRequests /></AppLayout></ProtectedAdminRoute>} />
          {/* SCAN-1: auto-deactivated dead-query manager. */}
          <Route path="/admin/dead-queries" element={<ProtectedAdminRoute><AppLayout><AdminDeadQueries /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/admin/dead-queries" element={<ProtectedAdminRoute><AppLayout><AdminDeadQueries /></AppLayout></ProtectedAdminRoute>} />
          {/* SCAN-2: external API cache hit/cost-saved stats. */}
          <Route path="/admin/api-cache-stats" element={<ProtectedAdminRoute><AppLayout><AdminApiCacheStats /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/admin/api-cache-stats" element={<ProtectedAdminRoute><AppLayout><AdminApiCacheStats /></AppLayout></ProtectedAdminRoute>} />
          {/* SCAN-4: scan caps + per-user daily budgets. */}
          <Route path="/admin/scan-budgets" element={<ProtectedAdminRoute><AppLayout><AdminScanBudgets /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/admin/scan-budgets" element={<ProtectedAdminRoute><AppLayout><AdminScanBudgets /></AppLayout></ProtectedAdminRoute>} />
          {/* SCAN-5: rolling quality scoring for lead_scanner_v2 queries. */}
          <Route path="/admin/scan-quality" element={<ProtectedAdminRoute><AppLayout><AdminScanQuality /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/admin/scan-quality" element={<ProtectedAdminRoute><AppLayout><AdminScanQuality /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/my-den" element={<AppLayout><MyDen /></AppLayout>} />
          {/* Safety alias: /dashboard/den (reported 404) -> the Founder Den. No nav uses it, but a stale link/bookmark must not dead-end. */}
          <Route path="/dashboard/den" element={<Navigate to="/dashboard/my-den" replace />} />
          <Route path="/dashboard/agendas" element={<AppLayout><AgendasPage /></AppLayout>} />
          <Route path="/dashboard/agendas/performance" element={<AppLayout><AgendasPage /></AppLayout>} />
          {/* INVESTOR-SUITE-INDEX (2026-05-27) — hub + sector breakdown + held-positions portfolio. */}
          <Route path="/dashboard/invest" element={<AppLayout><InvestHub /></AppLayout>} />
          {/* MY-COMPANIES-PAGE v0.01 (2026-05-30) — alias so the topbar "My Companies →"
              anchor (/dashboard/invest/overview#my-companies-section) resolves to InvestHub. */}
          <Route path="/dashboard/invest/overview" element={<AppLayout><InvestHub /></AppLayout>} />
          {/* FUNDRAISE-SIM v0.01 (2026-05-28) — founder-facing fundraise simulator built
              over the shared src/lib/fundraise engines. Lives in the existing
              /dashboard/invest/ prefix per the nav-change protocol (no new top-level
              entry). Useful to both founder mode and investor mode (an investor
              reviewing a founder's modeled raise). */}
          <Route path="/dashboard/invest/raising-sim" element={<AppLayout><RaisingSim /></AppLayout>} />
          {/* FUNDRAISE-SIM INVEST-VIEW v0.01 (2026-05-28) — investor-facing fund returns
              simulator over the same src/lib/fundraise engines. Positions against
              Tactyc / AngelList Projector but free + wired to our pipeline-investor-
              match data. Same /dashboard/invest/ prefix per the nav protocol. */}
          {/* INVEST-INVESTOR-GRAYWALL v0.01 (2026-05-29) — investor-only INVEST
              routes wrapped in <InvestorOnlyGate> so Pro tier sees the GrayWall
              upgrade card instead of the live page. Agency + Enterprise see
              the page normally. Free is redirected to /pricing. */}
          <Route path="/dashboard/invest/fund-sim" element={<AppLayout><InvestorOnlyGate featureName="Fund Sim" description="Model your fund the way LPs will model you — power-law Monte Carlo on portfolio construction, wired to real deal data." bullets={["Editable outcome distribution + percentile bar chart","Insights: shots on goal, optimal follow-on, portfolio construction guide","Honest thesis-fit dealflow linking back to your real Pipeline"]}><FundSim /></InvestorOnlyGate></AppLayout>} />
          <Route path="/dashboard/invest/sector-theses" element={<AppLayout><InvestSectorTheses /></AppLayout>} />
          <Route path="/dashboard/invest/portfolio" element={<AppLayout><InvestorOnlyGate featureName="Portfolio" description="The companies you've invested in. Track news, valuations, and your ownership — so you know when to double down or cash out." bullets={["Manual or imported holdings with $ size + entry date","Per-position news + signals from your scan data","One-page printable Tear Sheet"]}><InvestPortfolio /></InvestorOnlyGate></AppLayout>} />
          <Route path="/dashboard/invest/find-investors" element={<RequireAuth><AppLayout><FindInvestors /></AppLayout></RequireAuth>} />
          {/* Pipeline Match — read the pipeline-investor-match engine's ranked results (INVEST-PIPELINE-MATCH-SURFACE). */}
          <Route path="/dashboard/invest/pipeline-match" element={<AppLayout><PipelineMatch /></AppLayout>} />
          <Route path="/dashboard/invest/tweet-hunter" element={<AppLayout><TweetHunter /></AppLayout>} />
          <Route path="/dashboard/invest/warm-intros" element={<AppLayout><WarmIntros /></AppLayout>} />
          <Route path="/dashboard/my-thesis" element={<AppLayout><MyThesis /></AppLayout>} />
          <Route path="/dashboard/my-cap-table" element={<AppLayout><MyCapTable /></AppLayout>} />
          <Route path="/dashboard/anti-investors" element={<AppLayout><AntiInvestors /></AppLayout>} />
          <Route path="/dashboard/invest/circuit" element={<AppLayout><InvestorOnlyGate featureName="Investor Circuit" description="The investors you co-invest with most often — used to surface warm-intro paths and co-investment signals." bullets={["Co-invest pattern detection","Top connectors per thesis sector","Use the data to seed Warm Intros + Pipeline Match"]}><InvestorCircuit /></InvestorOnlyGate></AppLayout>} />
          <Route path="/dashboard/invest/import" element={<AppLayout><ImportInvestorsPage /></AppLayout>} />
          {/* INV-2 (2026-05-27): alias the dead /dashboard/invest/* paths that 404'd to a
              no-match blank (mistaken for a "hang") onto their real, live surfaces. The
              destination pages are fully functional — investor-circuit lives at .../circuit,
              the vetting hub at /dashboard/vetting-suite, anti-portfolio at the passed-deals
              pipeline tab, and dealflow at the canonical pipeline-CRM. No live page changed. */}
          <Route path="/dashboard/invest/investor-circuit" element={<Navigate to="/dashboard/invest/circuit" replace />} />
          <Route path="/dashboard/invest/vetting-suite" element={<Navigate to="/dashboard/vetting-suite" replace />} />
          <Route path="/dashboard/invest/anti-portfolio" element={<Navigate to="/dashboard/pipeline?tab=passed" replace />} />
          <Route path="/dashboard/invest/dealflow" element={<Navigate to="/dashboard/pipeline-crm" replace />} />
          {/* INVEST-ROUTES-DEADLINK v0.01 (2026-06-08): /dashboard/invest/pipeline 404'd —
              no route existed. The live fundraise pipeline is the Fundraise tab of the
              pipeline-CRM (CrmPage reads ?tab=fundraise; TAB_VALUES includes "fundraise").
              Redirect the stale path onto that tab. No live page changed. */}
          <Route path="/dashboard/invest/pipeline" element={<Navigate to="/dashboard/pipeline-crm?tab=fundraise" replace />} />
          {/* INVEST-ROUTEMAP v0.01 (2026-05-28): /dashboard/<feature> shorthand
              aliases. Live-browser audit caught /dashboard/find-investors
              returning the SPA 404 — the nav links use /dashboard/invest/*
              but anyone typing the shorter path (Claude-chat, external
              links, Saul in the address bar) hit nothing. Redirect every
              INVEST surface's shorthand to its canonical /invest/* path.
              No new components — just route entries. */}
          <Route path="/dashboard/find-investors" element={<Navigate to="/dashboard/invest/find-investors" replace />} />
          <Route path="/dashboard/warm-intros" element={<Navigate to="/dashboard/invest/warm-intros" replace />} />
          <Route path="/dashboard/sector-theses" element={<Navigate to="/dashboard/invest/sector-theses" replace />} />
          {/* INVEST-IA v0.01 (2026-05-29): Portfolio Watch + Pulse folded into
              Signals as tabs. Shorthand paths redirect to the right tab. */}
          <Route path="/dashboard/portfolio-watch" element={<Navigate to="/dashboard/signals?tab=watching" replace />} />
          <Route path="/dashboard/portfolio-pulse" element={<Navigate to="/dashboard/signals?tab=digest" replace />} />
          <Route path="/dashboard/deal-memos" element={<Navigate to="/dashboard/invest/memos" replace />} />
          <Route path="/dashboard/linchpins" element={<Navigate to="/dashboard/invest/linchpins" replace />} />
          <Route path="/dashboard/investor-circuit" element={<Navigate to="/dashboard/invest/circuit" replace />} />
          <Route path="/dashboard/pipeline-match" element={<Navigate to="/dashboard/invest/pipeline-match" replace />} />
          <Route path="/dashboard/tweet-hunter" element={<Navigate to="/dashboard/invest/tweet-hunter" replace />} />
          {/* SmartEmpty admin (SEO/card-health): live 4-state health of every list-backed card. */}
          <Route path="/dashboard/admin/card-health" element={<ProtectedAdminRoute><AppLayout><CardHealth /></AppLayout></ProtectedAdminRoute>} />
          {/* What never reached the press feed: no route to the journalist, or a
              newsjack signal that is not a request at all. */}
          {/* Every message received at a machine address, what was made of it,
              and the links found in it. Nothing here is ever fetched. */}
          <Route path="/dashboard/admin/raw-intake" element={<ProtectedAdminRoute><AppLayout><RawIntake /></AppLayout></ProtectedAdminRoute>} />
          {/* The lessons the reply writer draws on, and the candidates waiting for
              approval. Nothing enters the store unreviewed. */}
          <Route path="/dashboard/admin/experience-store" element={<ProtectedAdminRoute><AppLayout><ExperienceStore /></AppLayout></ProtectedAdminRoute>} />
          {/* Founder Stack — founder-mode service-provider hub (coming-soon cards v0.01). */}
          <Route path="/dashboard/founder/stack" element={<AppLayout><FounderStack /></AppLayout>} />
          <Route path="/dashboard/artifacts" element={<AppLayout><ArtifactsPage /></AppLayout>} />
          <Route path="/dashboard/artifacts/needs-review" element={<AppLayout><NeedsReviewPage /></AppLayout>} />
          <Route path="/dashboard/artifacts/:id" element={<AppLayout><ArtifactsPage /></AppLayout>} />
          {/* Term sheet comparator — founder analysis of an incoming term sheet. */}
          <Route path="/dashboard/term-sheets/:id" element={<AppLayout><TermSheetComparator /></AppLayout>} />
          {/* INVEST-ROUTES-AUDIT v0.01 (2026-05-30): the sidebar links to bare
              /dashboard/invest/term-sheets but only /new + /:id had routes — the bare
              path hit the SPA 404. Redirect it to the authoring entry. */}
          <Route path="/dashboard/invest/term-sheets" element={<Navigate to="/dashboard/invest/term-sheets/new" replace />} />
          {/* Investor-side term sheet authoring + comparator (Dispatch G). */}
          <Route path="/dashboard/invest/term-sheets/new" element={<AppLayout><InvestorOnlyGate featureName="Term Sheets" description="Draft and compare term sheets side by side — see what a clause really costs before you sign." bullets={["NVCA-default term sheet authoring","Side-by-side comparison vs your past offers","Auto-flag predatory clauses + market deviation"]}><InvestorTermSheetAuthor /></InvestorOnlyGate></AppLayout>} />
          <Route path="/dashboard/invest/term-sheets/:id" element={<AppLayout><InvestorTermSheetAuthor /></AppLayout>} />
          {/* Portfolio company watchlist + Pulse digest — INVEST-IA v0.01
              (2026-05-29) folded into Signals as tabs. Old direct routes
              redirect into the unified Signals page so bookmarks survive. */}
          <Route path="/dashboard/invest/portfolio-watch" element={<Navigate to="/dashboard/signals?tab=watching" replace />} />
          <Route path="/dashboard/invest/portfolio-pulse" element={<Navigate to="/dashboard/signals?tab=digest" replace />} />
          {/* Investor Pulse Watch — track investors' posts for thesis keywords. */}
          {/* INVEST nav v0.04: Pulse Watch merged into Portfolio Pulse as a tab. */}
          <Route path="/dashboard/invest/pulse-watch" element={<Navigate to="/dashboard/invest/portfolio-pulse?tab=watch" replace />} />
          {/* Deal Memos — investment memo authoring (Dispatch K). */}
          <Route path="/dashboard/invest/memos" element={<AppLayout><InvestorOnlyGate featureName="Deal Memos" description="The structured memo behind every yes — your thesis, your concerns, the path you'd take." bullets={["Auto-generated context_pack from your scan + signals data","Weighted conviction + deterministic action band","Linchpins panel — asymmetric plays only you can run"]}><DealMemos /></InvestorOnlyGate></AppLayout>} />
          {/* INVEST-UPDATES v0.01 (2026-05-29): Visible.vc-style cap-table updates. */}
          <Route path="/dashboard/invest/updates" element={<AppLayout><InvestorUpdates /></AppLayout>} />
          <Route path="/dashboard/invest/diligence" element={<AppLayout><DiligenceRooms /></AppLayout>} />
          {/* INVEST-DECKS v0.01 (2026-05-29): Papermark-style tracked deck shares. */}
          <Route path="/dashboard/invest/decks" element={<AppLayout><InvestorDecks /></AppLayout>} />
          <Route path="/deck/:slug" element={<DeckShareViewer />} />
          {/* INVEST-FOUNDER-RADAR v0.01 (2026-05-29): pattern-match over existing scan data. */}
          <Route path="/dashboard/invest/founder-radar" element={<AppLayout><InvestorOnlyGate featureName="Founder Radar" description="Founders before they show up in Crunchbase. We watch ~600 daily signals across platforms for the patterns that say 'someone just started something'." bullets={["7 pattern detectors (YC, stealth, big-co departure, raising, co-founder hunt, just-launched, GitHub-active)","Score 0-100 with recency bonus","One-click Add to Pipeline / Research / Find warm intros"]}><FounderRadar /></InvestorOnlyGate></AppLayout>} />
          {/* INVEST-TEARSHEET v0.01 (2026-05-29): one-page printable portfolio summary. */}
          <Route path="/dashboard/invest/portfolio/tearsheet" element={<AppLayout><InvestorOnlyGate featureName="Portfolio Tear Sheet" description="Your portfolio at a glance — positions, allocations, recent activity per company. Print it. Show it to an LP." bullets={["Recharts allocation pie + per-position legend","Per-holding recent-signals strip","Print-friendly @media print rules — drops cleanly to letter"]}><PortfolioTearSheet /></InvestorOnlyGate></AppLayout>} />
          {/* INVEST-DATAROOM v0.01 (2026-05-29): multi-doc tracked rooms. */}
          <Route path="/dashboard/invest/datarooms" element={<AppLayout><InvestorDatarooms /></AppLayout>} />
          <Route path="/room/:slug" element={<DataroomShareViewer />} />
          {/* INVEST-BRIEF v0.01 (2026-05-29): pre-meeting investor one-pager. */}
          <Route path="/dashboard/invest/brief/:investor_id" element={<AppLayout><InvestorBrief /></AppLayout>} />
          {/* INVEST-OUTREACH-TEMPLATES v0.01 (2026-05-29): reusable outreach templates. */}
          <Route path="/dashboard/invest/outreach-templates" element={<AppLayout><OutreachTemplates /></AppLayout>} />
          <Route path="/dashboard/invest/memos/:id" element={<AppLayout><DealMemoDetail /></AppLayout>} />
          {/* Linchpins — asymmetric plays only you can run (LINCHPINS-V0-1). */}
          <Route path="/dashboard/invest/linchpins" element={<AppLayout><InvestorOnlyGate featureName="Linchpins" description="Asymmetric plays only you can run — opportunities the crowd hasn't priced in yet." bullets={["Synthesized thesis vs your verified investor pattern","Auto-generated play cards with the angle named","New plays whenever you click Refresh"]}><InvestLinchpins /></InvestorOnlyGate></AppLayout>} />
          {/* INVEST-area fallback: any UNMATCHED /dashboard/invest/* path renders a
              friendly "section not available" card instead of the generic 404 /
              a blank screen. More-specific invest routes above rank higher (v6),
              so this splat only catches genuinely-unmatched investing paths. */}
          <Route path="/dashboard/invest/*" element={<AppLayout><InvestSectionFallback /></AppLayout>} />
          {/* Calendar (ICS) connection + prebrief settings (Dispatch J). */}
          <Route path="/dashboard/settings/calendar" element={<AppLayout><CalendarSettings /></AppLayout>} />
          {/* HENHOUSE-V1: paste an X post URL, find 15+ similar door-opener posts. */}
          <Route path="/dashboard/henhouse" element={<AppLayout><HenhousePage /></AppLayout>} />
          <Route path="/dashboard/foxpicks" element={<AppLayout><FoxPicksHub /></AppLayout>} />
          {/* Launch Pack: pick communities, generate JIT posts. v1 overhaul 2026-05-10. */}
          <Route path="/dashboard/launch-pack" element={<AppLayout><LaunchPack /></AppLayout>} />
          <Route path="/dashboard/admin/launch-pack-quality" element={<ProtectedAdminRoute><AppLayout><AdminLaunchPackQuality /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/admin/cron-truth" element={<ProtectedAdminRoute><AppLayout><AdminCronTruth /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/admin/money" element={<ProtectedAdminRoute><AppLayout><AdminMoney /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/admin/marketplace-order" element={<ProtectedAdminRoute><AppLayout><AdminMarketplaceOrders /></AppLayout></ProtectedAdminRoute>} />
          {/* DenDispatcher preview/test routes — additive, no overrides on
              existing /dashboard or /clients/:id pages until each Den is
              proven in preview mode. */}
          <Route path="/dashboard/dens" element={<AppLayout><MyDensList /></AppLayout>} />
          <Route path="/dashboard/dens/new" element={<AppLayout><AddDenPage /></AppLayout>} />
          <Route path="/dashboard/dens/:persona" element={<AppLayout><DenDispatcher mode="self" /></AppLayout>} />
          <Route path="/dashboard/dealflow" element={<AppLayout><Dealflow /></AppLayout>} />
          <Route path="/dashboard/foxdens" element={<AppLayout><FoxDensPage /></AppLayout>} />
          {/* /dashboard/outreach-review now points to the new multi-source
              page (Twitter v0.1, Bio Scanner, Dealflow Backfill, FoxPicks).
              The legacy closer_leads queue UI is preserved at
              /dashboard/closer-review for archeology. */}
          <Route path="/dashboard/outreach-review" element={<AppLayout><TwitterOutreachReview /></AppLayout>} />
          <Route path="/dashboard/twitter-outreach-review" element={<AppLayout><TwitterOutreachReview /></AppLayout>} />
          <Route path="/dashboard/closer-review" element={<AppLayout><OutreachReview /></AppLayout>} />
          {/* /sequences is the canonical full Sequences page; the old paths redirect here. */}
          <Route path="/sequences" element={<AppLayout><Sequences /></AppLayout>} />
          <Route path="/dashboard/sequences" element={<Navigate to="/sequences" replace />} />
          <Route path="/dashboard/mentions" element={<AppLayout><Mentions /></AppLayout>} />
          <Route path="/dashboard/insights" element={<AppLayout><Insights /></AppLayout>} />
          <Route path="/dashboard/comp" element={<AppLayout><CompBuilder /></AppLayout>} />
          <Route path="/dashboard/applicants/screen" element={<AppLayout><ApplicantScreen /></AppLayout>} />
          {/* HR Tools subgroup — Phase 0 nav scaffold per Recruiter War Room v0.2 spec.
              Talent Finder is fully built (Phase 5); the rest land on a placeholder
              that announces the phase and ETA per Rule 4 (no fake CTAs). */}
          <Route path="/dashboard/hr-tools/talent-finder" element={<AppLayout><TalentFinderPage /></AppLayout>} />
          <Route path="/dashboard/hr-tools/stealth-approach" element={<AppLayout><StealthApproachPage /></AppLayout>} />
          <Route path="/dashboard/hr-tools/bulk-compare" element={<AppLayout><BulkCompareCandidatesPage /></AppLayout>} />
          <Route path="/dashboard/hr-tools/bulk-compare/:jobId" element={<AppLayout><BulkCompareResultsPage /></AppLayout>} />
          <Route path="/comparison-bulk/:shareToken" element={<BulkCompareResultsPage readOnly={true} />} />
          <Route path="/dashboard/hr-tools/talent-crm" element={<AppLayout><TalentCrmPage /></AppLayout>} />
          <Route path="/dashboard/hr-tools/:slug" element={<AppLayout><HrToolsPlaceholder /></AppLayout>} />
          <Route path="/dashboard/admin/be-my-guest" element={<ProtectedAdminRoute><AppLayout><AdminBeMyGuest /></AppLayout></ProtectedAdminRoute>} />
          {/* Walkthrough-as-Spec v0 (2026-05-10) */}
          <Route path="/dashboard/admin/walkthrough-specs" element={<ProtectedAdminRoute><AppLayout><AdminWalkthroughSpecs /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/walkthroughs" element={<AppLayout><Walkthroughs /></AppLayout>} />
          <Route path="/investor-vetter" element={<AppLayout><InvestorVetter /></AppLayout>} />
          {/* T853: one-off checks from the public pages ("Check this investor" / "Check this founder"); /dashboard/* is served by the app, the /investor-vetter and /founder-vetter URLs redirect to the marketing pages. */}
          <Route path="/dashboard/investor-check" element={<AppLayout><InvestorVetter /></AppLayout>} />
          <Route path="/dashboard/founder-check" element={<AppLayout><FounderVetter /></AppLayout>} />
          <Route path="/investor-vetter/dossier" element={<AppLayout><InvestorDossier /></AppLayout>} />
          <Route path="/investor-vetter/compare" element={<AppLayout><InvestorCompare /></AppLayout>} />
          <Route path="/investor-vetter/term-sheet" element={<AppLayout><TermSheetAnalyzer /></AppLayout>} />
          <Route path="/investor-vetter/meeting-prep" element={<AppLayout><MeetingPrep /></AppLayout>} />
          <Route path="/investor-vetter/pitch-deck" element={<AppLayout><PitchDeckReview /></AppLayout>} />
          {/* CC#173: Read-only browse of MentionFox's investor index. */}
          <Route path="/investor-vetter/browse" element={<AppLayout><InvestorBrowse /></AppLayout>} />
          <Route path="/dashboard/investor-vetter/browse" element={<AppLayout><InvestorBrowse /></AppLayout>} />
          <Route path="/clients/:id/investor-vetter/browse" element={<AppLayout><InvestorBrowse /></AppLayout>} />
          {/* Alumni Spinout Tracker — Phase 1 (2026-05-11). PARITY surface
              served by the same component (useParams resolves clientId
              for the per-client view). */}
          <Route path="/dashboard/investor-vetter/alumni-watchlists" element={<AppLayout><AlumniWatchlists /></AppLayout>} />
          <Route path="/investor-vetter/alumni-watchlists" element={<AppLayout><AlumniWatchlists /></AppLayout>} />
          <Route path="/clients/:id/alumni" element={<AppLayout><AlumniWatchlists /></AppLayout>} />
          <Route path="/clients/:id/investor-vetter/alumni-watchlists" element={<AppLayout><AlumniWatchlists /></AppLayout>} />
          <Route path="/vetting/methodology/founder" element={<AppLayout><FounderVetter /></AppLayout>} />
          {/* INFLUENCER-VETTER (2026-06-07): deep creator due-diligence */}
          <Route path="/influencer-vetter" element={<AppLayout><InfluencerVetter /></AppLayout>} />
          {/* ACC-1: accelerator vetter */}
          <Route path="/accelerator-vetter" element={<AppLayout><AcceleratorVetter /></AppLayout>} />
          {/* YC-SCORER-1: 10-dimension application scorer + public share card */}
          <Route path="/yc-application-scorer" element={<AppLayout><YcApplicationScorer /></AppLayout>} />
          <Route path="/yc-application-scorer/score/:token" element={<YcScoreCard />} />
          {/* FOUNDER-SELF: self-vet — Founder Vetter pipeline with self-view framing */}
          <Route path="/founder-self-vetter" element={<AppLayout><FounderSelfVetter /></AppLayout>} />
          {/* INV-MATCH-2: Founder Fit matcher — same component handles both anon and authed entry. */}
          <Route path="/founder-fit" element={<AppLayout><FounderFit /></AppLayout>} />
          <Route path="/dashboard/founder-fit" element={<AppLayout><FounderFit /></AppLayout>} />
          <Route path="/founder-fit/share/:token" element={<FounderFitShare />} />
          {/* YC-1: public landing + auth-gated content */}
          <Route path="/yc-report" element={<YcReportLanding />} />
          <Route path="/dashboard/yc-report" element={<AppLayout><YcReportContent /></AppLayout>} />
          {/* JOURNALIST-VETTER-RENAME (2026-05-17): customer-facing name is
              "Journalist Vetter"; /journalist-vetter is now the canonical
              route. /journalism-person-vetter retained as a redirect so
              existing external bookmarks + report share URLs still work.
              /source-vetter still deep-links to use_case=cite_as_source.
              Internal subject_type='journalism_person' is unchanged. */}
          {/* SOURCE-CHECK (2026-07-27): Journalism suite and Vetting Suite both
              land here; the page scrolls the tool itself into view rather than
              the top of the page. */}
          <Route path="/dashboard/source-check" element={<AppLayout><SourceCheck /></AppLayout>} />
          <Route path="/journalist-vetter" element={<AppLayout><JournalismPersonVetter /></AppLayout>} />
          <Route path="/journalism-person-vetter" element={<Navigate to="/journalist-vetter" replace />} />
          <Route path="/source-vetter" element={<Navigate to="/journalist-vetter?use_case=cite_as_source" replace />} />
          {/* Vetters 0.16 (2026-05-09): the following 11 vetter routes are
              archived. Their page components were removed; deep-links now
              redirect to the dashboard so existing emails / PDFs / customer
              support links don't 404. To revive any of them, pull the page
              from docs/archived-vetters/2026-05-09/. */}
          <Route path="/penny-stock-vetter" element={<Navigate to="/dashboard" replace />} />
          <Route path="/publication-vetter" element={<AppLayout><PublicationVetter /></AppLayout>} />
          <Route path="/executive-vetter" element={<AppLayout><ExecutiveVetter /></AppLayout>} />
          <Route path="/wealth-advisor-vetter" element={<AppLayout><WealthAdvisorVetter /></AppLayout>} />
          <Route path="/physician-vetter" element={<AppLayout><PhysicianVetter /></AppLayout>} />
          <Route path="/pe-firm-vetter" element={<AppLayout><PeFirmVetter /></AppLayout>} />
          <Route path="/company-vetter" element={<AppLayout><CompanyVetter /></AppLayout>} />
          <Route path="/vc-firm-vetter" element={<AppLayout><VcFirmVetter /></AppLayout>} />
          {/* T789: Claims Check (was Verification Vetter); old paths 301 in vercel.json, these Navigates cover in-app links */}
          <Route path="/claims-check" element={<AppLayout><ClaimsCheck /></AppLayout>} />
          <Route path="/claims-check/job/:job_id" element={<AppLayout><ClaimsCheckJob /></AppLayout>} />
          <Route path="/verification-vetter" element={<Navigate to="/claims-check" replace />} />
          <Route path="/verification-vetter/job/:job_id" element={<LegacyClaimsJobRedirect />} />
          <Route path="/dashboard/manuscript/:id" element={<AppLayout><ManuscriptVerificationView /></AppLayout>} />
          <Route path="/entrepreneur-vetter" element={<Navigate to="/vetting/methodology/founder" replace />} />
          <Route path="/test/paginated-report" element={<AppLayout><PaginatedReportTest /></AppLayout>} />
          {/* MARKETS-RETIRE (2026-07-24): the legacy /markets section is off-thesis
              (old social-listening framing). Per-page disposition, not a blanket
              redirect:
                /markets                        → /vetting          (hub, off-thesis)
                /markets/pipeline-watch         → the INVEST investor-side Pipeline
                                                  Watch, which already exists at
                                                  /dashboard/pipeline?tab=watchlist
                                                  and mounts the SAME component
                /markets/pipeline-watch/{research,coach,portfolio}
                                                → kept alive under /dashboard/pipeline/watch/*
                                                  (below). These are working
                                                  Pipeline Watch features, not
                                                  off-thesis pages — sending them
                                                  to /vetting would delete them.
              Vercel also 301s these at the edge (vercel.json); these in-app
              Navigates keep client-side navigation consistent. */}
          <Route path="/markets" element={<Navigate to="/vetting" replace />} />
          <Route path="/markets/pipeline-watch" element={<Navigate to="/dashboard/pipeline?tab=watchlist" replace />} />
          <Route path="/markets/pipeline-watch/research" element={<Navigate to="/dashboard/pipeline/watch/research" replace />} />
          <Route path="/markets/pipeline-watch/coach" element={<Navigate to="/dashboard/pipeline/watch/coach" replace />} />
          <Route path="/markets/pipeline-watch/portfolio" element={<Navigate to="/dashboard/pipeline/watch/portfolio" replace />} />
          {/* New canonical homes for the Pipeline Watch sub-tools. */}
          <Route path="/dashboard/pipeline/watch/research" element={<AppLayout><PipelineWatchResearch /></AppLayout>} />
          <Route path="/dashboard/pipeline/watch/coach" element={<AppLayout><PipelineWatchCoach /></AppLayout>} />
          <Route path="/dashboard/pipeline/watch/portfolio" element={<AppLayout><PipelineWatchPortfolio /></AppLayout>} />
          {/* NAV-WAVE-1 (2026-05-19): consolidated hub routes + redirects
              from the old URLs to the new ?tab= deep-links. Each hub
              wraps existing page components — Saul's data flows are
              untouched, only nav grouping changes. */}
          <Route path="/dashboard/pipeline" element={<AppLayout><PipelineHubPage /></AppLayout>} />
          <Route path="/dashboard/pipeline-watch" element={<Navigate to="/dashboard/pipeline?tab=watchlist" replace />} />
          <Route path="/dashboard/anti-portfolio" element={<Navigate to="/dashboard/pipeline?tab=passed" replace />} />
          <Route path="/dashboard/alumni-tracker" element={<Navigate to="/dashboard/pipeline?tab=alumni" replace />} />
          <Route path="/dashboard/sector-theses" element={<AppLayout><SectorThesesPage /></AppLayout>} />
          <Route path="/dashboard/portfolio" element={<AppLayout><PortfolioPage /></AppLayout>} />
          <Route path="/dashboard/signals" element={<AppLayout><SignalsHubPage /></AppLayout>} />
          <Route path="/dashboard/signal-stream" element={<Navigate to="/dashboard/signals?tab=live" replace />} />
          <Route path="/dashboard/outreach" element={<AppLayout><OutreachHubPage /></AppLayout>} />
          <Route path="/dashboard/stealth-approach" element={<Navigate to="/dashboard/outreach?tab=stealth" replace />} />
          <Route path="/dashboard/saved-content" element={<AppLayout><SavedContent /></AppLayout>} />
          <Route path="/dashboard/candidate-eval" element={<Navigate to="/dashboard/evaluate" replace />} />
          <Route path="/dashboard/profile" element={<Navigate to="/settings/profile" replace />} />
          {/* Stale deep-link guard: nav repointed to /settings/profile (ProfileSettings, Mode switcher from Dispatch D); redirect old path so bookmarks don't 404. */}
          <Route path="/dashboard/my-profile" element={<Navigate to="/settings/profile" replace />} />
          <Route path="/dashboard/help" element={<Navigate to="/help" replace />} />
          <Route path="/dashboard/foxhole" element={<AppLayout><Foxhole /></AppLayout>} />
          <Route path="/dashboard/foxhole/pipeline" element={<AppLayout><FoxholeDashboardShell /></AppLayout>} />
          <Route path="/dashboard/foxhole/voice" element={<AppLayout><FoxholeVoiceSetup /></AppLayout>} />
          {/* DECK-QUESTIONS-1 (2026-05-16): pitch deck → investor questions. */}
          <Route path="/dashboard/foxhole/deck-questions" element={<DeckQuestionsPage />} />
          {/* ANTI-INVESTMENT-TRACKER (2026-05-16): Bessemer-style passed-on showcase. */}
          <Route path="/dashboard/foxhole/anti-portfolio" element={<AntiPortfolioPage />} />
          {/* PROMOTER-BRAIN-SIGNAL-WATCH (2026-05-16): inflection signals on watched companies. */}
          <Route path="/dashboard/foxhole/signals" element={<SignalStreamPage />} />
          {/* MARKETS-RETIRE: the three /markets/pipeline-watch/* mounts moved to
              /dashboard/pipeline/watch/* (declared above, alongside the redirects
              that point at them). */}
          {/* NAV-WAVE-1: /dashboard/markets redirects into SignalsHub's
              markets tab. Deep URLs at /dashboard/markets/crypto/... are
              registered below and stay unchanged. */}
          <Route path="/dashboard/markets" element={<Navigate to="/dashboard/signals?tab=markets" replace />} />
          <Route path="/dashboard/markets/crypto/compare" element={<AppLayout><MarketsCompareCryptoPage /></AppLayout>} />
          {/* v0.4 canonical: /dashboard/markets/crypto/:chain/:identifier */}
          <Route path="/dashboard/markets/crypto/:chain/:identifier" element={<AppLayout><MarketsCryptoTokenPage /></AppLayout>} />
          {/* Legacy v0.1: /dashboard/markets/crypto/:mint — redirects to /sol/:mint inside MarketsCryptoTokenPage */}
          <Route path="/dashboard/markets/crypto/:mint" element={<AppLayout><MarketsCryptoTokenPage /></AppLayout>} />
          <Route path="/dashboard/cc-queue" element={<ProtectedAdminRoute><AppLayout><CCQueue /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/settings" element={<AppLayout><Settings /></AppLayout>} />
          <Route path="/dashboard/admin" element={<ProtectedAdminRoute><AdminFounderAlerts /></ProtectedAdminRoute>} />
          <Route path="/dashboard/sites" element={<AppLayout><Sites /></AppLayout>} />
          <Route path="/settings/profile" element={<AppLayout><ProfileSettings /></AppLayout>} />
          <Route path="/settings/foxchat" element={<AppLayout><FoxChatPreferences /></AppLayout>} />
          <Route path="/settings/mcp" element={<AppLayout><McpSettings /></AppLayout>} />
          <Route path="/dashboard/sales-arsenal" element={<AppLayout><SalesArsenal /></AppLayout>} />
          <Route path="/dashboard/capture-forms" element={<AppLayout><CaptureForms /></AppLayout>} />
          {/* /dashboard/videos route removed per Saul 2026-04-22. */}
          {/* Not in main nav — screenshot-share page for Saul's LinkedIn outreach. */}
          <Route path="/dashboard/proof" element={<AppLayout><Proof /></AppLayout>} />
          {/* Public standalone pages — no AppLayout, no auth required. */}
          <Route path="/form/:slug" element={<CaptureForm />} />
          <Route path="/v/:id" element={<VideoView />} />
          <Route path="/settings/company-profile" element={<AppLayout><CompanyProfile /></AppLayout>} />
          <Route path="/dashboard/company-profile" element={<AppLayout><CompanyProfile /></AppLayout>} />
          <Route path="/settings/brands" element={<AppLayout><SettingsBrands /></AppLayout>} />
          <Route path="/dashboard/settings/brands" element={<AppLayout><SettingsBrands /></AppLayout>} />
          <Route path="/dashboard/reports" element={<AppLayout><Reports /></AppLayout>} />
          {/* NAV Restructure (2026-05-11) — master pages. */}
          <Route path="/dashboard/vetting-suite" element={<AppLayout><VettingSuiteMaster /></AppLayout>} />
          <Route path="/dashboard/setup" element={<AppLayout><SetupMaster /></AppLayout>} />
          <Route path="/dashboard/reports/:id" element={<AppLayout><ReportDetail /></AppLayout>} />
          <Route path="/dashboard/scans" element={<AppLayout><Scans /></AppLayout>} />
          <Route path="/dashboard/engage" element={<AppLayout><EngagementHQ /></AppLayout>} />
          <Route path="/dashboard/engage/telegram-setup" element={<AppLayout><TelegramSetup /></AppLayout>} />
          <Route path="/dashboard/engage/reddit-setup" element={<AppLayout><RedditSetup /></AppLayout>} />
          {/* Live-Demo Component (v0.1) — index keeps dashboard chrome,
              canvas is full-bleed (renders OUTSIDE AppLayout). */}
          <Route path="/dashboard/live-demo" element={<AppLayout><LiveDemoIndex /></AppLayout>} />
          <Route path="/dashboard/live-demo/:sessionId" element={<LiveDemoCanvas />} />
          {/* Public viewer — no auth required, RLS-gated by public_token. */}
          <Route path="/demo/:token" element={<PublicLiveDemoView />} />
          <Route path="/executive" element={<ProtectedAdminRoute><AppLayout><ExecutiveDashboard /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/scans/:scanId" element={<AppLayout><ScanResults /></AppLayout>} />
          <Route path="/dashboard/foxtrails/:mentionId" element={<AppLayout><FoxtrailResults /></AppLayout>} />
          <Route path="/dashboard/embed" element={<AppLayout><Embed /></AppLayout>} />
          <Route path="/dashboard/saved" element={<AppLayout><SavedContent /></AppLayout>} />
          <Route path="/dashboard/slider-builder" element={<AppLayout><SliderBuilder /></AppLayout>} />
          <Route path="/dashboard/comparison-card" element={<AppLayout><ComparisonCardGenerator /></AppLayout>} />
          <Route path="/dashboard/trend-chart" element={<AppLayout><TrendChartGenerator /></AppLayout>} />
          <Route path="/arsenal" element={<AppLayout><SurfaceUnavailable name="The shared arsenal" /></AppLayout>} />
          <Route path="/dashboard/mentioners" element={<AppLayout><Mentioners /></AppLayout>} />
          <Route path="/dashboard/contacts" element={<AppLayout><Contacts /></AppLayout>} />
          <Route path="/dashboard/find" element={<AppLayout><FindHub /></AppLayout>} />
          {/* Legacy /dashboard/people-finder → consolidated People+Companies hub. */}
          <Route path="/dashboard/people-finder" element={<Navigate to="/dashboard/find?tab=people" replace />} />
          {/* Multichannel Send Component v0.1 (primitive #8) — single
              video, multiple delivery surfaces, one record. Naked path
              creates a new send from query params, :id loads existing. */}
          <Route path="/dashboard/send" element={<AppLayout><MultichannelSendPage /></AppLayout>} />
          <Route path="/dashboard/send/:id" element={<AppLayout><MultichannelSendPage /></AppLayout>} />
          {/* RULE 12 — Identity-First Data Ratchet approve queue. Photos
              + logos with provenance issues land here for Saul to
              approve / reject. Nightly cron (04 UTC) populates. */}
          <Route path="/dashboard/data-quality" element={<ProtectedAdminRoute><AppLayout><DataQualityPage /></AppLayout></ProtectedAdminRoute>} />
          {/* Lead Finder v0.0 dry-run review surface — Saul-only (gated
              at the component level + RLS on lead_cluster_plans_draft). */}
          <Route path="/dashboard/admin/lead-clusters" element={<ProtectedAdminRoute><AppLayout><AdminLeadClustersPage /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/dossiers" element={<AppLayout><SavedDossiers /></AppLayout>} />
          {/* Full-page dossier (reuses the same DossierSections renderer as the drawer/public view). */}
          <Route path="/dashboard/dossier/:profileId" element={<AppLayout><DossierPage /></AppLayout>} />
          {/* NAV Polish v2 — alias paths keep existing bookmarks live. */}
          <Route path="/dashboard/saved-dossiers" element={<AppLayout><SavedContent /></AppLayout>} />
          {/* NAV-WAVE-1: /dashboard/compare redirects into FindHub's
              compare tab. CompareHubV2 still mounts there inline. */}
          <Route path="/dashboard/compare" element={<Navigate to="/dashboard/find?tab=compare" replace />} />
          <Route path="/dashboard/compare-people" element={<AppLayout><ComparePeoplePage /></AppLayout>} />
          {/* RECRUIT-NAV (2026-06-02): /dashboard/hr-tools/bulk-compare is the
              canonical Bulk Compare route (results child lives under it). The
              old /dashboard/bulk-compare now redirects to it — one component,
              one canonical route. */}
          <Route path="/dashboard/bulk-compare" element={<Navigate to="/dashboard/hr-tools/bulk-compare" replace />} />
          {/* Compare Companies v0.1 — replaces the prior "coming soon" stub.
              The CompareCompanies stub component stays in /pages for any
              external bookmark that wanted to read the placeholder copy. */}
          {/* Legacy /dashboard/compare-companies → consolidated hub.
              COMPARE-MERGE (2026-07-24): was pointing at /dashboard/compare?tab=companies,
              which redirected again to /dashboard/find?tab=compare and dropped the
              sub-tab on the way — so this link always landed on People. Now a single
              hop straight to the Companies sub-tab. */}
          <Route path="/dashboard/compare-companies" element={<Navigate to="/dashboard/find?tab=compare&sub=companies" replace />} />
          <Route path="/comparison-companies/:shareToken" element={<CompanyComparisonReportPage />} />
          {/* Founder Application Suite Sprint 1 — directory / detail / review. */}
          <Route path="/apply-to" element={<AppLayout><AppToTargetsDirectory /></AppLayout>} />
          <Route path="/apply-to/:target_id" element={<AppLayout><AppToTargetDetail /></AppLayout>} />
          <Route path="/apply-to/draft/:draft_id" element={<AppLayout><AppDraftReview /></AppLayout>} />
          {/* Legacy /dashboard/company-finder → consolidated Find hub. */}
          <Route path="/dashboard/company-finder" element={<Navigate to="/dashboard/find?tab=companies" replace />} />
          <Route path="/dashboard/evaluate" element={<AppLayout><CandidateEvaluatePage /></AppLayout>} />
          <Route path="/dashboard/evaluate/compare" element={<AppLayout><EvaluationBundleComparisonPage /></AppLayout>} />
          <Route path="/dashboard/influencer-calculator" element={<AppLayout><InfluencerCalculator /></AppLayout>} />
          <Route path="/dashboard/landing-pages" element={<AppLayout><LandingPages /></AppLayout>} />
          <Route path="/dashboard/content" element={<AppLayout><ContentRepurposing /></AppLayout>} />
          <Route path="/dashboard/content-creator" element={<AppLayout><ContentRepurposing /></AppLayout>} />
          <Route path="/dashboard/content-creator/inbox" element={<AppLayout><StudioInbox /></AppLayout>} />
          <Route path="/dashboard/content/inbox" element={<AppLayout><StudioInbox /></AppLayout>} />
          <Route path="/dashboard/content-creator/clipper" element={<AppLayout><ContentCreatorClipper /></AppLayout>} />
          <Route path="/dashboard/content/clipper" element={<AppLayout><ContentCreatorClipper /></AppLayout>} />
          <Route path="/dashboard/content-creator/brutal-truth-cards" element={<AppLayout><BrutalTruthCards /></AppLayout>} />
          <Route path="/dashboard/content/brutal-truth-cards" element={<AppLayout><BrutalTruthCards /></AppLayout>} />
          <Route path="/dashboard/content-creator/shorts-composer/:job_id" element={<AppLayout><ShortsComposer /></AppLayout>} />
          <Route path="/dashboard/content/shorts-composer/:job_id" element={<AppLayout><ShortsComposer /></AppLayout>} />
          <Route path="/dashboard/influencer-eval" element={<AppLayout><InfluencerEvalPage /></AppLayout>} />
          <Route path="/dashboard/compare/reports" element={<AppLayout><CompareHub /></AppLayout>} />
          {/* NAV v2 (2026-05-11) — /dashboard/team redirects into Dashboard
              sub-tab. Standalone Team page kept at /dashboard/my-team for
              the agency-team-management workflow. */}
          <Route path="/dashboard/team" element={<Navigate to="/dashboard?tab=team" replace />} />
          <Route path="/dashboard/my-team" element={<AppLayout><SurfaceUnavailable name="Team management" /></AppLayout>} />
          <Route path="/dashboard/portals" element={<AppLayout><SurfaceUnavailable name="Client portals" /></AppLayout>} />
          <Route path="/portal/:slug" element={<SurfaceUnavailable name="Client portals" insteadHref="/" insteadLabel="Go to mentionfox.com" />} />
          <Route path="/dashboard/monthly-reports" element={<AppLayout><SurfaceUnavailable name="Monthly reports" /></AppLayout>} />
          <Route path="/dashboard/custom-roles" element={<AppLayout><SurfaceUnavailable name="Custom roles" /></AppLayout>} />
          <Route path="/dashboard/audit-logs" element={<AppLayout><SurfaceUnavailable name="Audit logs" /></AppLayout>} />
          <Route path="/dashboard/brand-kits" element={<AppLayout><BrandKits /></AppLayout>} />
          <Route path="/dashboard/blog-generator" element={<AppLayout><BlogGenerator /></AppLayout>} />
          <Route path="/dashboard/faq-generator" element={<AppLayout><SurfaceUnavailable name="The FAQ generator" /></AppLayout>} />
          <Route path="/dashboard/content-library" element={<AppLayout><ContentLibrary /></AppLayout>} />
          <Route path="/dashboard/geo-seo" element={<AppLayout><GeoSeoHub /></AppLayout>} />
          {/* Legacy /dashboard/geofixer → consolidated GEO + SEO hub. */}
          <Route path="/dashboard/geofixer" element={<Navigate to="/dashboard/geo-seo?tab=geo" replace />} />
          {/* RETIRED 2026-08-07. Conversation-leading and citation-article production
              were both tested at scale and neither moved the outcome it was built for.
              See the DO-NOT-REVIVE section of CLAUDE.md before rebuilding any of this.
              Bookmarks redirect to the GEO hub rather than dead-ending. */}
          <Route path="/dashboard/geofixer/autopilot" element={<Navigate to="/dashboard/geo-seo?tab=geo" replace />} />
          <Route path="/dashboard/geofixer/foxfix" element={<Navigate to="/dashboard/geo-seo?tab=geo" replace />} />
          <Route path="/dashboard/geofixer/articles" element={<Navigate to="/dashboard/geo-seo?tab=geo" replace />} />
          <Route path="/dashboard/geofixer/indexnow" element={<AppLayout><IndexNow /></AppLayout>} />
          <Route path="/dashboard/geofixer/crawler-analytics" element={<AppLayout><GeoFixerCrawlerAnalytics /></AppLayout>} />
          <Route path="/dashboard/geofixer/anthropic-audit" element={<ProtectedAdminRoute><AppLayout><AnthropicGeoAudit /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/admin/earned-media" element={<ProtectedAdminRoute><AppLayout><AdminEarnedMedia /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/press" element={<AppLayout><Press /></AppLayout>} />
          {/* Inbound press — journalists seeking sources (shared press_requests feed). */}
          <Route path="/dashboard/press-requests" element={<AppLayout><PressRequests /></AppLayout>} />
          {/* Newsjack — detect breaking stories to tie the user's expertise to. */}
          <Route path="/dashboard/newsjack" element={<AppLayout><Newsjack /></AppLayout>} />
          <Route path="/dashboard/admin/seo-submission-helper" element={<ProtectedAdminRoute><AppLayout><AdminSeoSubmissionHelper /></AppLayout></ProtectedAdminRoute>} />
          {/* CL-5 — SUPERADMIN-prefixed alias for the same surface; linked from the AdminSidebar SEO section. */}
          <Route path="/admin/seo-submissions" element={<ProtectedAdminRoute><AdminSeoSubmissionHelper /></ProtectedAdminRoute>} />
          {/* MEDIUM-ARTICLES-SYSTEM v0.01 — admin drafting/publishing surface. */}
          <Route path="/admin/medium-articles" element={<ProtectedAdminRoute><AppLayout><MediumArticles /></AppLayout></ProtectedAdminRoute>} />
          {/* GEOFIXER-STRATEGY-B-FOUNDATION + SEOFIXER-AUTOPILOT admin pages. */}
          <Route path="/admin/next-steps" element={<ProtectedAdminRoute><AppLayout><NextSteps /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/admin/decisions" element={<ProtectedAdminRoute><AppLayout><Decisions /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/admin/hallucinations" element={<ProtectedAdminRoute><AppLayout><Hallucinations /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/admin/geo-briefs" element={<ProtectedAdminRoute><AppLayout><GeoBriefs /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/admin/citation-roi" element={<ProtectedAdminRoute><AppLayout><CitationRoi /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/admin/seo-indexation" element={<ProtectedAdminRoute><AppLayout><SeoIndexation /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/admin/page-proposals" element={<ProtectedAdminRoute><AppLayout><PageProposals /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/admin/page-review" element={<ProtectedAdminRoute><AppLayout><PageReview /></AppLayout></ProtectedAdminRoute>} />
          {/* The front-door bench, beside page-review because it is the same job: a human judging what the product said. */}
          <Route path="/dashboard/admin/showwork-bench" element={<ProtectedAdminRoute><AppLayout><ShowworkBench /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/admin/featured-submissions" element={<ProtectedAdminRoute><AppLayout><FeaturedSubmissions /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/admin/company-claims" element={<ProtectedAdminRoute><AppLayout><CompanyClaims /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/mcp-token" element={<AppLayout><McpToken /></AppLayout>} />
          <Route path="/admin/seo-autopilot-history" element={<ProtectedAdminRoute><AppLayout><SeoAutopilotHistory /></AppLayout></ProtectedAdminRoute>} />
          {/* SH-2 — Public search page powered by unified-search edge function. */}
          <Route path="/search" element={<Search />} />
          <Route path="/clients/:id/geo/crawler-analytics" element={<AppLayout><ClientCrawlerAnalytics /></AppLayout>} />
          {/* Legacy /dashboard/seofixer → consolidated GEO + SEO hub. */}
          <Route path="/dashboard/seofixer" element={<Navigate to="/dashboard/geo-seo?tab=seo" replace />} />
          <Route path="/clients/:id/seo" element={<AppLayout><SeoFixerClient /></AppLayout>} />
          <Route path="/dashboard/settings/white-label" element={<AppLayout><WhiteLabelSettings /></AppLayout>} />
          <Route path="/dashboard/settings/connections" element={<AppLayout><SettingsConnections /></AppLayout>} />
          <Route path="/dashboard/settings/engagement-controls" element={<AppLayout><EngagementControls /></AppLayout>} />
          <Route path="/dashboard/settings/bio-keywords" element={<AppLayout><BioKeywords /></AppLayout>} />
          <Route path="/dashboard/settings/extension" element={<AppLayout><SettingsExtension /></AppLayout>} />
          <Route path="/dashboard/settings/email" element={<AppLayout><EmailSettingsPage /></AppLayout>} />
          <Route path="/dashboard/settings/sending-identity" element={<AppLayout><SendingIdentityPage /></AppLayout>} />
          <Route path="/dashboard/settings/api-keys" element={<AppLayout><SettingsApiKeys /></AppLayout>} />
          <Route path="/dashboard/settings/platforms" element={<AppLayout><SettingsPlatforms /></AppLayout>} />
          <Route path="/dashboard/settings/foxpicks" element={<AppLayout><SettingsFoxpicks /></AppLayout>} />
          <Route path="/dashboard/settings/voice" element={<AppLayout><VoiceSettings /></AppLayout>} />
          <Route path="/dashboard/launch-tools/tagline" element={<AppLayout><TaglineLab /></AppLayout>} />
          <Route path="/dashboard/brand-kit" element={<AppLayout><BrandKit /></AppLayout>} />
          <Route path="/dashboard/settings/outreach-sender" element={<AppLayout><OutreachSender /></AppLayout>} />
          <Route path="/dashboard/lead-pool" element={<ProtectedAdminRoute><AppLayout><LeadPool /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/admin/seo-submissions" element={<ProtectedAdminRoute><AppLayout><SeoSubmissions /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/dashboard/geofixer/drafts" element={<Navigate to="/dashboard/geo-seo?tab=geo" replace />} />
          {/* Citation Leads retired 2026-06-03 — the outreach approach was dead
              (the authors AI cites are competitors/paid, nothing to gift).
              Routes redirect to the Den so old bookmarks/shares don't dead-end.
              The page component + citation_leads table are kept for history. */}
          <Route path="/dashboard/citation-leads" element={<Navigate to="/dashboard/my-den" replace />} />
          <Route path="/dashboard/setup/my-business" element={<AppLayout><SetupMyBusiness /></AppLayout>} />
          <Route path="/dashboard/geofixer/citation-leads" element={<Navigate to="/dashboard/my-den" replace />} />
          <Route path="/dashboard/clips" element={<AppLayout><Clips /></AppLayout>} />
          <Route path="/dashboard/clips/:id" element={<AppLayout><ClipDetail /></AppLayout>} />
          <Route path="/share/clip/:id" element={<ClipShare />} />
          <Route path="/share/geo/:token" element={<PublicGeoShare />} />
          <Route path="/share/:token" element={<SharePage />} />
          <Route path="/embed/clip/:id" element={<ClipEmbed />} />
          {/* Browser-extension Email Component popup. Specific path
              MUST come before the /embed/:widgetId catch-all below. */}
          <Route path="/embed/email-component" element={<EmbedEmailComponent />} />
          {/* Extension v1.0 Phase 1 — FoxOut Reply primitive. Loaded
              inside the extension's shadow-DOM modal via iframe. */}
          <Route path="/embed/foxout-reply" element={<EmbedFoxoutReply />} />
          {/* Profile Snapshot Component v0.1 (primitive #5). Renders
              full-mode in a chrome-less surface; consumed by extension
              dot-click modal in Phase 3 + by hover tooltips elsewhere. */}
          <Route path="/embed/profile-snapshot" element={<EmbedProfileSnapshot />} />
          <Route path="/embed/profile-snapshot/:id" element={<EmbedProfileSnapshot />} />
          {/* Extension v1.0 Phase 2B — Compare-with multi-select picker.
              Loaded by the extension's shadow-DOM modal when the user
              right-clicks a profile link → MentionFox → Compare with…. */}
          <Route path="/embed/compare-picker" element={<EmbedComparePicker />} />
          {/* /help is static HTML — no redirect needed */}
          <Route path="/embed/:widgetId" element={<EmbedViewer />} />
          <Route path="/widget/preview/:widgetId" element={<WidgetPreviewPage />} />
          <Route path="/preview/:slug" element={<SliderPreview />} />

          {/* All /help/*, /about, /terms, /api-docs, etc. are static HTML in /public/ — no React routes */}

          {/* Admin Routes — protected by superadmin role */}
          <Route path="/admin" element={<ProtectedAdminRoute><Admin /></ProtectedAdminRoute>} />
          <Route path="/admin/users" element={<ProtectedAdminRoute><AdminUsers /></ProtectedAdminRoute>} />
          <Route path="/admin/activity" element={<ProtectedAdminRoute><SurfaceUnavailable name="Admin activity" /></ProtectedAdminRoute>} />
          <Route path="/admin/feature-requests" element={<ProtectedAdminRoute><AdminFeatureRequests /></ProtectedAdminRoute>} />
          <Route path="/admin/foxchat-learning" element={<ProtectedAdminRoute><AdminFoxchatLearning /></ProtectedAdminRoute>} />
          <Route path="/admin/cost-debug" element={<ProtectedAdminRoute><AdminCostDebug /></ProtectedAdminRoute>} />
          <Route path="/admin/subscriptions" element={<ProtectedAdminRoute><AdminSubscriptions /></ProtectedAdminRoute>} />
          {/* /admin/impersonations route removed 2026-05-13 — no backing tables.
              AdminImpersonations component file kept on disk at
              src/pages/AdminImpersonations.tsx in case the feature is rewired. */}
          <Route path="/admin/security" element={<ProtectedAdminRoute><SurfaceUnavailable name="Security history" /></ProtectedAdminRoute>} />
          <Route path="/admin/alerts" element={<ProtectedAdminRoute><AdminAlerts /></ProtectedAdminRoute>} />
          <Route path="/admin/settings" element={<ProtectedAdminRoute><SurfaceUnavailable name="Site settings" /></ProtectedAdminRoute>} />
          {/* Public changelog. Both the page and the .json redirect existed but
              neither was ever routed, so /changelog 404d for everyone. */}
          <Route path="/changelog" element={<Changelog />} />
          <Route path="/changelog.json" element={<ChangelogJsonRedirect />} />
          <Route path="/admin/changelog" element={<ProtectedAdminRoute><AdminChangelog /></ProtectedAdminRoute>} />
          <Route path="/admin/fox-agents" element={<ProtectedAdminRoute><AdminFoxAgents /></ProtectedAdminRoute>} />

          {/* Owner Routes — wrapped in AppLayout (requireAuth=true) */}
          <Route path="/owner" element={<ProtectedAdminRoute><AppLayout><Owner /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/owner/health" element={<ProtectedAdminRoute><AppLayout><HealthDashboard /></AppLayout></ProtectedAdminRoute>} />
          <Route path="/owner/filtering-analytics" element={<ProtectedAdminRoute><AppLayout><FilteringAnalytics /></AppLayout></ProtectedAdminRoute>} />

          {/* CRM v0 — HubSpot displacement (2026-05-10). Schema in
              crm_* tables; full Pro+ workflow engine + inbox sync arrive in
              v0.2 / v0.1.1 respectively. */}
          {/* Investor Pipeline CRM v0.1 — full-page 5-tab surface
              (Pipeline / Inbox / Lists / Sequences / Team). Mounted at
              /dashboard/pipeline-crm to avoid colliding with the existing
              HubSpot-displacement CRM v0 at /dashboard/crm/*. */}
          <Route path="/dashboard/pipeline-crm" element={<AppLayout><PipelineCrmPage /></AppLayout>} />
          {/* Unified CRM (Phase A) — Contacts + Companies under one home. Does not
              disturb /dashboard/pipeline-crm (the live CRM); cross-links to it. */}
          <Route path="/dashboard/crm" element={<Navigate to="/dashboard/crm/contacts" replace />} />
          <Route path="/dashboard/crm/contacts" element={<AppLayout><CrmHomePage /></AppLayout>} />
          {/* Phase 3 (2026-06-12): crm_companies now has 462 real rows from the
              person-first backfill, so the Companies surface renders the canonical
              layer (same CrmHomePage, Companies tab). Deals stays on dealflow (live). */}
          <Route path="/dashboard/crm/companies" element={<AppLayout><CrmHomePage /></AppLayout>} />
          <Route path="/dashboard/crm/deals" element={<Navigate to="/dashboard/dealflow" replace />} />
          <Route path="/dashboard/automations" element={<AppLayout><AutomationsPage /></AppLayout>} />
          <Route path="/dashboard/pipeline-crm/settings/sender" element={<AppLayout><SenderSettingsPage /></AppLayout>} />
          <Route path="/dashboard/crm" element={<Navigate to="/dashboard/crm/contacts" replace />} />
          <Route path="/dashboard/crm/contacts" element={<AppLayout><CrmContacts /></AppLayout>} />
          <Route path="/dashboard/crm/pipelines" element={<AppLayout><CrmPipelines /></AppLayout>} />
          <Route path="/dashboard/crm/sequences" element={<Navigate to="/sequences" replace />} />
          <Route path="/dashboard/command-center" element={<AppLayout><CommandCenter /></AppLayout>} />
          <Route path="/dashboard/api-webhooks" element={<AppLayout><ApiWebhooksPage /></AppLayout>} />
          {/* APIs & Integrations hub — folds Integrations + API & Webhooks + the
              FoxAPIs doorway into tabs (2026-07). Old routes above still resolve. */}
          <Route path="/dashboard/apis-integrations" element={<AppLayout><ApisIntegrationsHub /></AppLayout>} />
          <Route path="/dashboard/crm/lists" element={<AppLayout><CrmLists /></AppLayout>} />
          <Route path="/dashboard/crm/workflows" element={<AppLayout><CrmWorkflows /></AppLayout>} />
          <Route path="/dashboard/crm/inbox" element={<AppLayout><CrmInbox /></AppLayout>} />
          <Route path="/dashboard/crm/reports" element={<AppLayout><CrmReports /></AppLayout>} />
          <Route path="/dashboard/crm/import/hubspot" element={<AppLayout><CrmImportHubSpot /></AppLayout>} />
          {/* Call Commander v0 — phone-dialing-from-the-CRM (2026-05-10) */}
          <Route path="/dashboard/crm/call-commander" element={<AppLayout><CallCommander /></AppLayout>} />
          <Route path="/dashboard/crm/call-commander/settings" element={<AppLayout><CallCommanderSettings /></AppLayout>} />
          <Route path="/m/call-commander/:session_id" element={<CallCommanderMobile />} />

          {/* About + Team page generator — Bundle 0.2 (2026-05-10) */}
          <Route path="/dashboard/about-page-generator" element={<AppLayout><AboutPageGeneratorWizard /></AppLayout>} />
          <Route path="/dashboard/about-page-generator/:project_id" element={<AppLayout><AboutPageGeneratorWizard /></AppLayout>} />

          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="/dashboard/billing" element={<Navigate to="/dashboard/settings?tab=billing" replace />} />
          <Route path="/clients" element={<AppLayout><Brands /></AppLayout>} />
          <Route path="/clients/new" element={<AppLayout><NewBrand /></AppLayout>} />
          <Route path="/clients/:id" element={<AppLayout><BrandDetail /></AppLayout>} />
          {/* Athlete-Den deep link per spec §7. Re-uses the same BrandDetail
              page; the dashboard tab is the default, so no extra wiring is
              needed beyond the redirect-style route. */}
          <Route path="/clients/:id/den" element={<AppLayout><BrandDetail /></AppLayout>} />
          <Route path="/clients/:id/competitors" element={<AppLayout><ClientCompetitors /></AppLayout>} />
          <Route path="/dashboard/brands" element={<Navigate to="/clients" replace />} />
          <Route path="/brands" element={<Navigate to="/clients" replace />} />
          <Route path="/brands/new" element={<Navigate to="/clients/new" replace />} />
          <Route path="/dashboard/pricing" element={<Navigate to="/dashboard/settings?tab=billing" replace />} />
          <Route path="/dashboard/competitors" element={<AppLayout><Competitors /></AppLayout>} />
          <Route path="/dashboard/integrations" element={<AppLayout><Integrations /></AppLayout>} />
          <Route path="/dashboard/upcoming-meetings" element={<AppLayout><UpcomingMeetings /></AppLayout>} />
          <Route path="/dashboard/api" element={<Navigate to="/dashboard?upgrade=api_access" replace />} />
          {/* Mailbox connect return (2026-09-15): the redirect URI mailbox-connect registers with Google and Microsoft. */}
          <Route path="/auth/gmail-callback" element={<MailboxOAuthCallback />} />
          <Route path="/auth/outlook-callback" element={<MailboxOAuthCallback />} />
          <Route path="/settings/sender" element={<Navigate to="/dashboard/pipeline-crm/settings/sender" replace />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </ChunkErrorBoundary>
        <CookieConsent />
        </AdminViewProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
